import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, Settings, Phone, MessageCircle, Calendar, Flame, Trash2, ExternalLink, StickyNote, X, Edit, GripVertical, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Eye, List, LayoutGrid, UserPlus, Users, Zap, CheckCircle, FileDown, FileText, Loader2, CheckCircle2, Paperclip, Download, File, Image, FileSpreadsheet, Star, Filter } from 'lucide-react';
import { crmAPI, teamAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useSubscriptionLimits } from '../hooks/useSubscriptionLimits';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import Layout from '../components/Layout';
import { useUserCard } from '../hooks/useUserCard';
import { useSidebar } from '../contexts/SidebarContext';
import TeamInvitationBanner from '../components/TeamInvitationBanner';
import { formatCurrency, formatAmount } from '../utils/numberFormat';

// Add custom styles for vertical text
const verticalTextStyle = {
  writingMode: 'vertical-rl',
  textOrientation: 'mixed',
  transform: 'rotate(180deg)'
};

// Helper function to get gradient style for pipeline stages based on actual stage color
const getStageGradientStyle = (color) => {
  // Check if it's a gray/light color (last stage default)
  const grayColors = ['#CCCCCC', '#cccccc', '#e5e7eb', '#E5E7EB', '#d1d5db', '#D1D5DB'];
  if (grayColors.includes(color)) {
    return {
      background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)'
    };
  }
  // Use the actual stage color to create a gradient
  return {
    background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`
  };
};

// Common currencies with symbols
const COMMON_CURRENCIES = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'SAR', symbol: 'ر.س', name: 'Saudi Riyal' }
];

const CRM = () => {
  const location = useLocation();
  const { userCard, loading: cardLoading } = useUserCard();
  const { isCollapsed, setIsCollapsed } = useSidebar();
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showAddStageDialog, setShowAddStageDialog] = useState(false);
  const [showEditStageDialog, setShowEditStageDialog] = useState(false);
  const [showManageStagesDialog, setShowManageStagesDialog] = useState(false);
  const [showAddTemplateDialog, setShowAddTemplateDialog] = useState(false);
  const [showFollowUpDialog, setShowFollowUpDialog] = useState(false);
  const [showViewNotesDialog, setShowViewNotesDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEditDealDialog, setShowEditDealDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [contactDetails, setContactDetails] = useState(null);
  const [leadContactInfo, setLeadContactInfo] = useState(null);
  const [showAddDealDialog, setShowAddDealDialog] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [searchedContacts, setSearchedContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedStageForNewDeal, setSelectedStageForNewDeal] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    // Load saved view mode from localStorage
    const savedViewMode = localStorage.getItem('crmViewMode');
    return savedViewMode || 'kanban'; // default to kanban
  });
  const [editingStage, setEditingStage] = useState(null);
  const [newStageName, setNewStageName] = useState('');
  const [newStageColor, setNewStageColor] = useState('#eab308'); // Yellow color
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateMessage, setNewTemplateMessage] = useState('');
  const [newTemplateStageId, setNewTemplateStageId] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpNote, setFollowUpNote] = useState('');
  const [dealName, setDealName] = useState('');
  const [dealDescription, setDealDescription] = useState('');
  const [dealAmount, setDealAmount] = useState('');
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [dealClosingDate, setDealClosingDate] = useState('');
  const [dealProbability, setDealProbability] = useState('');
  const [dealCurrency, setDealCurrency] = useState('');
  const [dealCurrencyRate, setDealCurrencyRate] = useState('1');
  const [collapsedStages, setCollapsedStages] = useState(() => {
    // Load collapsed state from localStorage on mount
    const saved = localStorage.getItem('crmCollapsedStages');
    return saved ? JSON.parse(saved) : {};
  });
  const [swipeState, setSwipeState] = useState({
    leadId: null,
    startX: 0,
    currentX: 0,
    isSwiping: false
  });
  
  // Team-related states
  const [team, setTeam] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [leadToAssign, setLeadToAssign] = useState(null);
  const [assignmentNote, setAssignmentNote] = useState('');
  const [selectedAssignee, setSelectedAssignee] = useState('');
  
  // Pipeline states
  const [pipelines, setPipelines] = useState([]);
  const [activePipeline, setActivePipeline] = useState(null);
  const [pipelineTemplates, setPipelineTemplates] = useState([]);
  const [showPipelineDialog, setShowPipelineDialog] = useState(false);
  const [showCreatePipelineDialog, setShowCreatePipelineDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customPipelineName, setCustomPipelineName] = useState('');
  
  // Custom fields states
  const [customFields, setCustomFields] = useState([]);
  const [showCustomFieldsDialog, setShowCustomFieldsDialog] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [newFieldOptions, setNewFieldOptions] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [removingReminder, setRemovingReminder] = useState(false);
  
  // Filter state - array for multi-select
  const [filterAssignees, setFilterAssignees] = useState([]);
  const [showAssigneeFilter, setShowAssigneeFilter] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const [timeFilter, setTimeFilter] = useState('all'); // 'all', 'this_month', 'next_month', 'after_2_months'
  
  // Advanced filter state
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const [filterConditions, setFilterConditions] = useState([]);
  const [filterLogic, setFilterLogic] = useState('and'); // 'and' or 'or'
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [filteredLeadIds, setFilteredLeadIds] = useState(null); // null = no filter, array = filtered IDs
  
  // AI WhatsApp Message states
  const [showAIMessageDialog, setShowAIMessageDialog] = useState(false);
  const [aiGeneratedMessage, setAiGeneratedMessage] = useState('');
  const [generatingMessage, setGeneratingMessage] = useState(false);
  
  // Subscription limits
  const { limits, loading: limitsLoading, checkLimit, getLimitInfo, isSubscribed } = useSubscriptionLimits();
  
  // Import pipeline states
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const importFileInputRef = React.useRef(null);
  
  // Deal documents states
  const [dealDocuments, setDealDocuments] = useState([]);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const docFileInputRef = React.useRef(null);
  
  // Deal detail tabs
  const [detailTab, setDetailTab] = useState('details');
  
  // Lead stats for free user limits
  const [leadStats, setLeadStats] = useState(null);
  
  const { toast } = useToast();

  // Get currency symbol from team profile, default to ₹
  const getCurrencySymbol = () => {
    return team?.profile?.currency_symbol || '₹';
  };

  // Format deal amount with currency conversion
  const formatDealAmount = (lead, showBothAmounts = true) => {
    if (!lead.dealAmount) return null;
    
    const baseCurrency = getCurrencySymbol();
    const amount = parseFloat(lead.dealAmount);
    
    // If no custom currency, just show the base amount
    if (!lead.currency) {
      return formatCurrency(amount, baseCurrency);
    }
    
    // Get currency symbol from COMMON_CURRENCIES
    const currencyInfo = COMMON_CURRENCIES.find(c => c.code === lead.currency);
    const currencySymbol = currencyInfo?.symbol || lead.currency;
    const rate = parseFloat(lead.currencyRate) || 1;
    const convertedAmount = amount * rate;
    
    if (showBothAmounts) {
      // Show both: USD 1,000 (₹83,000)
      return (
        <>
          {currencySymbol}{formatAmount(amount)} 
          <span className="text-xs opacity-75"> ({formatCurrency(convertedAmount, baseCurrency)})</span>
        </>
      );
    } else {
      // Show only converted amount
      return formatCurrency(convertedAmount, baseCurrency);
    }
  };

  // Generate AI-powered WhatsApp message
  const generateAIMessage = async (lead) => {
    setGeneratingMessage(true);
    setShowAIMessageDialog(true);
    
    try {
      // Get current stage info
      const currentStage = stages.find(s => s.id === lead.stageId);
      const stageName = currentStage?.name || 'Unknown Stage';
      
      // Prepare context for AI
      const context = {
        contactName: lead.name || 'Valued Client',
        companyName: lead.company || '',
        dealName: lead.dealName || 'your requirement',
        dealAmount: lead.dealAmount ? formatCurrency(lead.dealAmount, getCurrencySymbol()) : null,
        currentStage: stageName,
        followUpDate: lead.followUpDate ? new Date(lead.followUpDate).toLocaleDateString() : null,
        lastContacted: lead.lastContacted ? new Date(lead.lastContacted).toLocaleDateString() : null,
        notes: lead.notes && lead.notes.length > 0 ? lead.notes[lead.notes.length - 1].text : null
      };

      // Create a professional, contextual message based on stage
      let message = `Dear ${context.contactName},\n\n`;

      // Stage-specific messages
      switch (stageName.toLowerCase()) {
        case 'new lead':
        case 'new':
          message += `I hope this message finds you well.\n\n`;
          message += `Thank you for your interest in ${context.dealName}. I would be delighted to understand your requirements better and explore how we might assist you.\n\n`;
          if (context.dealAmount) {
            message += `Our solutions start from ${context.dealAmount}, and I'm confident we can tailor something that perfectly meets your needs.\n\n`;
          }
          message += `Would you be available for a brief conversation at your convenience?\n\n`;
          message += `I look forward to connecting with you soon.`;
          break;

        case 'contacted':
        case 'in progress':
          message += `I trust you are doing well.\n\n`;
          message += `I wanted to follow up on our recent conversation regarding ${context.dealName}. `;
          message += `I hope you had an opportunity to review the information we discussed.\n\n`;
          if (context.followUpDate) {
            message += `As we had planned, I'm reaching out to see if you have any questions or require any clarifications.\n\n`;
          }
          message += `Please feel free to reach out at any time. I'm here to assist you in any way I can.`;
          break;

        case 'qualified':
          message += `I'm pleased to share some wonderful news with you.\n\n`;
          message += `We're ready to move forward with ${context.dealName}. `;
          if (context.dealAmount) {
            message += `The total investment would be ${context.dealAmount}, and I believe this represents excellent value for you.\n\n`;
          } else {
            message += `\n\n`;
          }
          message += `I have prepared a comprehensive proposal for your review. Would it be convenient to schedule a meeting where I can walk you through the details?\n\n`;
          message += `I'm excited about the possibility of working together.`;
          break;

        case 'proposal':
        case 'proposal sent':
          message += `I hope you've had the opportunity to review the proposal for ${context.dealName}.\n\n`;
          message += `I wanted to reach out to see if you have any questions or if there are any aspects you'd like to discuss in greater detail.\n\n`;
          if (context.dealAmount) {
            message += `The total investment of ${context.dealAmount} includes everything we discussed, and we're happy to work with you on a payment structure that suits your requirements.\n\n`;
          }
          message += `Would you be available for a brief call? I'd be delighted to address any questions you may have.`;
          break;

        case 'negotiation':
          message += `Thank you for your continued interest in ${context.dealName}.\n\n`;
          message += `I understand you may have some questions or would like to discuss adjustments to our proposal. `;
          message += `I'm fully committed to finding a solution that works perfectly for you.\n\n`;
          message += `Could we schedule a conversation to explore this further? I'm confident we can reach an arrangement that benefits us both.`;
          break;

        case 'closed won':
        case 'won':
          message += `Congratulations, and a warm welcome!\n\n`;
          message += `I'm delighted that we'll be working together on ${context.dealName}. `;
          message += `Thank you for placing your trust in us.\n\n`;
          message += `Our team is already making preparations to begin. You'll receive all the necessary details shortly.\n\n`;
          message += `I look forward to a successful and rewarding partnership.`;
          break;

        case 'closed lost':
        case 'lost':
          message += `Thank you for considering us for ${context.dealName}.\n\n`;
          message += `While we understand you've chosen a different path, we genuinely appreciate the time you invested in evaluating our services.\n\n`;
          message += `We would be honored to stay in touch and would be delighted to assist with any future requirements you may have. `;
          message += `Your feedback would be most valuable to us.`;
          break;

        default:
          message += `I hope you're doing well.\n\n`;
          message += `I wanted to touch base regarding ${context.dealName}. `;
          message += `Please let me know if there's anything I can do to assist or if you have any questions.\n\n`;
          message += `I look forward to hearing from you at your convenience.`;
      }

      // Add signature
      message += `\n\nBest regards,\n`;
      if (team?.profile?.owner_name) {
        message += `${team.profile.owner_name}\n`;
      }
      if (team?.profile?.company_name) {
        message += `${team.profile.company_name}`;
      }

      setAiGeneratedMessage(message);
    } catch (error) {
      console.error('Error generating AI message:', error);
      toast({
        title: "Error",
        description: "Failed to generate message. Please try again.",
        variant: "destructive"
      });
      setShowAIMessageDialog(false);
    } finally {
      setGeneratingMessage(false);
    }
  };

  // Send WhatsApp message
  const sendWhatsAppMessage = (phone, message) => {
    const formattedPhone = phone.replace(/[^\d]/g, '');
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${formattedPhone}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  // Auto-minimize sidebar when CRM loads
  useEffect(() => {
    if (!isCollapsed) {
      setIsCollapsed(true);
      localStorage.setItem('sidebarCollapsed', 'true');
    }
  }, []);

  // Save collapsed state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('crmCollapsedStages', JSON.stringify(collapsedStages));
  }, [collapsedStages]);

  // Save view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('crmViewMode', viewMode);
  }, [viewMode]);

  // Load only essential data on mount (lazy load rest when needed)
  useEffect(() => {
    const loadInitialData = async () => {
      // Load essential data: active pipeline, CRM data (stages, leads), and team (for permissions)
      await Promise.all([
        loadActivePipeline(), // Only active pipeline, not all pipelines
        fetchData(), // Stages and leads only
        loadTeam() // Team data for permission checks
      ]);
    };
    loadInitialData();
  }, []);

  // Log userCard for debugging filter issues
  useEffect(() => {
    if (userCard) {
      console.log('✅ UserCard loaded for filters:', {
        userId: userCard.userId,
        username: userCard.username
      });
    } else if (!cardLoading) {
      console.warn('⚠️ UserCard not loaded - "Assigned to me" filter may not work');
    }
  }, [userCard, cardLoading]);

  // Handle "Add to CRM" from Contacts page
  useEffect(() => {
    if (location.state?.addDealContact && stages.length > 0 && !loading) {
      const contactData = location.state.addDealContact;
      
      console.log('🔄 Opening Add Deal dialog from Contacts page:', contactData);
      
      // Pre-fill the contact information
      setSelectedContact(contactData);
      setDealName('');
      setDealDescription('');
      setDealAmount('');
      
      // Set first stage of active pipeline
      if (stages.length > 0) {
        setSelectedStageForNewDeal(stages[0].id);
      }
      
      // Open the Add Deal dialog
      setShowAddDealDialog(true);
      
      // Clear the navigation state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, stages, loading]);

  // Check for notification-triggered lead opening
  useEffect(() => {
    const openLeadId = sessionStorage.getItem('openLeadId');
    if (openLeadId && leads.length > 0) {
      console.log('🔔 Opening lead from notification:', openLeadId);
      // Find the lead and open its detail dialog
      const lead = leads.find(l => l.id === openLeadId);
      if (lead) {
        console.log('✅ Found lead:', lead.name);
        handleOpenDetailDialog(lead);
      } else {
        console.warn('⚠️ Lead not found:', openLeadId);
      }
      // Clear the sessionStorage
      sessionStorage.removeItem('openLeadId');
    }
  }, [leads]); // Run when leads are loaded

  // Load custom fields when active pipeline changes
  useEffect(() => {
    if (activePipeline) {
      loadCustomFields();
    }
  }, [activePipeline?.id]);

  // Load only active pipeline (lightweight initial load)
  const loadActivePipeline = async () => {
    try {
      const pipelinesData = await crmAPI.getPipelines();
      const active = pipelinesData.find(p => p.is_active);
      setActivePipeline(active || pipelinesData[0] || null);
      console.log('✅ Active pipeline loaded:', active?.name);
    } catch (error) {
      console.error('Error loading active pipeline:', error);
    }
  };

  // Load all pipelines and templates (lazy - only when needed)
  const loadPipelines = async () => {
    try {
      const [pipelinesData, templatesData] = await Promise.all([
        crmAPI.getPipelines(),
        crmAPI.getPipelineTemplates()
      ]);
      setPipelines(pipelinesData);
      setPipelineTemplates(templatesData);
      console.log('✅ All pipelines loaded:', pipelinesData.length);
      
      // Update active pipeline if not set
      if (!activePipeline) {
        const active = pipelinesData.find(p => p.is_active);
        setActivePipeline(active || pipelinesData[0] || null);
      }
    } catch (error) {
      console.error('Error loading pipelines:', error);
    }
  };

  // Switch pipeline
  const handleSwitchPipeline = async (pipelineId) => {
    try {
      await crmAPI.activatePipeline(pipelineId);
      toast({
        title: 'Success',
        description: 'Pipeline switched successfully'
      });
      setShowPipelineDialog(false);
      
      // Reload active pipeline and data
      await Promise.all([
        loadActivePipeline(),
        fetchData()
      ]);
    } catch (error) {
      console.error('Error switching pipeline:', error);
      toast({
        title: 'Error',
        description: 'Failed to switch pipeline',
        variant: 'destructive'
      });
    }
  };

  // Create new pipeline
  const handleCreatePipeline = async () => {
    if (!selectedTemplate) {
      toast({
        title: 'Error',
        description: 'Please select a template',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Create the pipeline and get the response with new pipeline data
      const newPipeline = await crmAPI.createPipeline(selectedTemplate, customPipelineName || null);
      
      // Activate the newly created pipeline to navigate to it
      if (newPipeline && newPipeline.id) {
        await crmAPI.activatePipeline(newPipeline.id);
      }
      
      toast({
        title: 'Success',
        description: `Pipeline "${newPipeline?.name || 'New Pipeline'}" created and activated`
      });
      setShowCreatePipelineDialog(false);
      setSelectedTemplate(null);
      setCustomPipelineName('');
      
      // Reload pipelines list and active pipeline - this will show the new pipeline
      await loadPipelines();
    } catch (error) {
      console.error('Error creating pipeline:', error);
      
      // Handle subscription required error (402)
      if (error.response?.status === 402) {
        setShowCreatePipelineDialog(false);
        
        // Staff users should not see upgrade modal - backend already sent appropriate message
        if (team && team.my_role === 'staff') {
          toast({
            title: 'Subscription Required',
            description: error.response?.data?.detail?.message || 'Your team owner needs an active Pro or Business subscription to create pipelines.',
            variant: 'destructive'
          });
        } else {
          // Owners redirected to pricing page
          window.location.href = '/pricing';
        }
        return;
      }
      
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create pipeline',
        variant: 'destructive'
      });
    }
  };

  // Delete pipeline
  const handleDeletePipeline = async (pipelineId) => {
    if (!confirm('Are you sure you want to delete this pipeline? All leads and stages will be permanently deleted.')) {
      return;
    }

    try {
      await crmAPI.deletePipeline(pipelineId);
      toast({
        title: 'Success',
        description: 'Pipeline deleted successfully'
      });
      
      // Reload pipelines and data
      await Promise.all([
        loadPipelines(),
        fetchData()
      ]);
    } catch (error) {
      console.error('Error deleting pipeline:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete pipeline',
        variant: 'destructive'
      });
    }
  };

  // Fetch only essential CRM data (stages and leads)
  const fetchData = async () => {
    try {
      setLoading(true);
      const [stagesData, leadsData, statsData] = await Promise.all([
        crmAPI.getStages(),
        teamAPI.getTeamLeads(),
        crmAPI.getLeadStats()
      ]);
      setStages(stagesData);
      setLeads(leadsData);
      setLeadStats(statsData);
      console.log('✅ CRM data loaded:', stagesData.length, 'stages,', leadsData.length, 'leads');
      if (statsData) {
        console.log('✅ Lead stats:', statsData);
      }
    } catch (error) {
      console.error('Error fetching CRM data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load CRM data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Lazy load team data (only when opening assign/filter dialog)
  const loadTeam = async () => {
    // Skip if already loaded
    if (team) {
      console.log('✅ Team data already loaded');
      return;
    }
    
    try {
      const teamData = await teamAPI.getMyTeam();
      console.log('✅ Team data loaded:', teamData);
      console.log('  - Team members:', teamData?.members?.length || 0);
      setTeam(teamData);
    } catch (error) {
      console.error('❌ Error loading team:', error);
      console.log('  - No team found, showing default filter options only');
      // No team is fine, user might not be in a team
    }
  };

  // Lazy load templates (only when opening template dialog)
  const loadTemplates = async () => {
    // Skip if already loaded
    if (templates.length > 0) {
      console.log('✅ Templates already loaded');
      return;
    }
    
    try {
      const templatesData = await crmAPI.getTemplates();
      setTemplates(templatesData);
      console.log('✅ Templates loaded:', templatesData.length);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleOpenAssignDialog = async (lead) => {
    setLeadToAssign(lead);
    setSelectedAssignee(lead.assigned_to || '');
    setAssignmentNote('');
    setShowAssignDialog(true);
    
    // Lazy load team data when opening assign dialog
    await loadTeam();
  };

  const handleAssignLead = async () => {
    if (!leadToAssign) return;

    try {
      await teamAPI.assignLead(
        leadToAssign.id,
        selectedAssignee || null,
        assignmentNote
      );
      
      // Update local state instead of refetching
      setLeads(leads.map(l => 
        l.id === leadToAssign.id 
          ? { ...l, assigned_to: selectedAssignee || null }
          : l
      ));
      
      toast({
        title: 'Success',
        description: selectedAssignee ? 'Lead assigned successfully' : 'Lead unassigned successfully'
      });
      
      setShowAssignDialog(false);
      setLeadToAssign(null);
      setSelectedAssignee('');
      setAssignmentNote('');
    } catch (error) {
      console.error('Error assigning lead:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to assign lead',
        variant: 'destructive'
      });
    }
  };

  // Handle stage change from dropdown (in detail dialog)
  const handleMoveLead = async (leadId, oldStageId, newStageId) => {
    // If same stage, do nothing
    if (oldStageId === newStageId) return;

    // Get stage names for toast message
    const oldStageName = stages.find(s => s.id === oldStageId)?.name || 'Unknown';
    const newStageName = stages.find(s => s.id === newStageId)?.name || 'Unknown';

    // Update UI optimistically
    const updatedLeads = [...leads];
    const leadIndex = updatedLeads.findIndex(l => l.id === leadId);
    if (leadIndex !== -1) {
      updatedLeads[leadIndex] = {
        ...updatedLeads[leadIndex],
        stageId: newStageId
      };
      setLeads(updatedLeads);
      
      // Update selectedLead if it's the same lead
      if (selectedLead?.id === leadId) {
        setSelectedLead({
          ...selectedLead,
          stageId: newStageId
        });
      }
    }

    // Update backend
    try {
      await crmAPI.updateLeadStage(leadId, newStageId, 0); // Position 0 (top of new stage)
      toast({
        title: 'Stage Updated',
        description: `Moved from "${oldStageName}" to "${newStageName}"`
      });
    } catch (error) {
      console.error('Error updating lead stage:', error);
      toast({
        title: 'Error',
        description: 'Failed to change stage',
        variant: 'destructive'
      });
      // Revert on error
      fetchData();
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination, draggableId, type } = result;

    // If dropped in the same position
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Handle stage column reordering
    if (type === 'STAGE') {
      const updatedStages = Array.from(stages);
      const [movedStage] = updatedStages.splice(source.index, 1);
      updatedStages.splice(destination.index, 0, movedStage);

      // Update positions
      const reorderedStages = updatedStages.map((stage, index) => ({
        id: stage.id,
        position: index
      }));

      // Optimistically update UI
      setStages(updatedStages.map((s, i) => ({ ...s, position: i })));

      try {
        await crmAPI.reorderStages(reorderedStages);
        toast({
          title: 'Success',
          description: 'Stages reordered successfully'
        });
      } catch (error) {
        console.error('Error reordering stages:', error);
        toast({
          title: 'Error',
          description: 'Failed to reorder stages',
          variant: 'destructive'
        });
        fetchData(); // Revert on error
      }
      return;
    }

    // Handle lead/deal dragging between stages (type === 'LEAD' or undefined for backwards compatibility)
    const leadId = draggableId;
    const newStageId = destination.droppableId;
    const newPosition = destination.index;

    // Update UI optimistically
    const updatedLeads = [...leads];
    const leadIndex = updatedLeads.findIndex(l => l.id === leadId);
    if (leadIndex !== -1) {
      updatedLeads[leadIndex] = {
        ...updatedLeads[leadIndex],
        stageId: newStageId,
        position: newPosition
      };
      setLeads(updatedLeads);
    }

    // Update backend
    try {
      await crmAPI.updateLeadStage(leadId, newStageId, newPosition);
      toast({
        title: 'Success',
        description: 'Deal moved successfully'
      });
    } catch (error) {
      console.error('Error updating lead:', error);
      toast({
        title: 'Error',
        description: 'Failed to move deal',
        variant: 'destructive'
      });
      // Revert on error
      fetchData();
    }
  };

  const toggleStageCollapse = (stageId) => {
    setCollapsedStages(prev => ({
      ...prev,
      [stageId]: !prev[stageId]
    }));
  };

  const calculateStageTotal = (stageId) => {
    const stageLeads = getLeadsForStage(stageId);
    const total = stageLeads.reduce((sum, lead) => {
      const amount = parseFloat(lead.dealAmount) || 0;
      // If deal has a different currency, convert it using the rate
      if (lead.currency && lead.currencyRate) {
        return sum + (amount * parseFloat(lead.currencyRate));
      }
      return sum + amount;
    }, 0);
    return total;
  };

  // Swipe gesture handlers for mobile
  const handleTouchStart = (e, lead) => {
    setSwipeState({
      leadId: lead.id,
      startX: e.touches[0].clientX,
      currentX: e.touches[0].clientX,
      isSwiping: true
    });
  };

  const handleTouchMove = (e, lead) => {
    if (swipeState.isSwiping && swipeState.leadId === lead.id) {
      setSwipeState(prev => ({
        ...prev,
        currentX: e.touches[0].clientX
      }));
    }
  };

  const handleTouchEnd = async (lead) => {
    if (!swipeState.isSwiping) return;

    const swipeDistance = swipeState.currentX - swipeState.startX;
    const minSwipeDistance = 50; // Minimum distance to trigger swipe

    if (Math.abs(swipeDistance) > minSwipeDistance) {
      // Find current stage index
      const currentStageIndex = stages.findIndex(s => s.id === lead.stageId);
      
      if (currentStageIndex === -1) {
        setSwipeState({ leadId: null, startX: 0, currentX: 0, isSwiping: false });
        return;
      }

      let targetStageIndex = -1;
      
      // Swipe right = move to next stage (right)
      if (swipeDistance > 0 && currentStageIndex < stages.length - 1) {
        targetStageIndex = currentStageIndex + 1;
      }
      // Swipe left = move to previous stage (left)
      else if (swipeDistance < 0 && currentStageIndex > 0) {
        targetStageIndex = currentStageIndex - 1;
      }

      if (targetStageIndex !== -1) {
        const targetStage = stages[targetStageIndex];
        
        // Update UI optimistically
        const updatedLeads = [...leads];
        const leadIndex = updatedLeads.findIndex(l => l.id === lead.id);
        if (leadIndex !== -1) {
          updatedLeads[leadIndex] = {
            ...updatedLeads[leadIndex],
            stageId: targetStage.id
          };
          setLeads(updatedLeads);
        }

        // Update backend
        try {
          await crmAPI.updateLeadStage(lead.id, targetStage.id, 0);
          toast({
            title: 'Lead Moved',
            description: `Moved to ${targetStage.name}`,
          });
        } catch (error) {
          console.error('Error moving lead:', error);
          toast({
            title: 'Error',
            description: 'Failed to move lead',
            variant: 'destructive'
          });
          // Revert on error
          fetchData();
        }
      }
    }

    // Reset swipe state
    setSwipeState({ leadId: null, startX: 0, currentX: 0, isSwiping: false });
  };

  const handleToggleImportant = async (lead) => {
    // Optimistic update - no need to refetch all data
    const updatedLeads = leads.map(l => l.id === lead.id ? { ...l, isImportant: !l.isImportant } : l);
    setLeads(updatedLeads);

    try {
      await crmAPI.updateLead(lead.id, { isImportant: !lead.isImportant });
      toast({
        title: 'Success',
        description: lead.isImportant ? 'Removed from hot leads' : 'Marked as hot lead'
      });
    } catch (error) {
      console.error('Error updating lead:', error);
      // Revert on error
      setLeads(leads);
      toast({
        title: 'Error',
        description: 'Failed to update lead',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    // Optimistic update
    const originalLeads = [...leads];
    setLeads(leads.filter(l => l.id !== leadId));

    try {
      await crmAPI.deleteLead(leadId);
      toast({
        title: 'Success',
        description: 'Lead deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting lead:', error);
      // Revert on error
      setLeads(originalLeads);
      toast({
        title: 'Error',
        description: 'Failed to delete lead',
        variant: 'destructive'
      });
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;

    try {
      const result = await crmAPI.addNote(selectedLead.id, noteText);
      
      // Update local state instead of refetching all data
      const updatedLeads = leads.map(l => {
        if (l.id === selectedLead.id) {
          return {
            ...l,
            notes: result.notes || [...(l.notes || []), { text: noteText, createdAt: new Date().toISOString() }]
          };
        }
        return l;
      });
      setLeads(updatedLeads);
      
      // Update selectedLead if dialog is still open
      if (selectedLead) {
        setSelectedLead({
          ...selectedLead,
          notes: result.notes || [...(selectedLead.notes || []), { text: noteText, createdAt: new Date().toISOString() }]
        });
      }
      
      toast({
        title: 'Success',
        description: 'Note added successfully'
      });
      setNoteText('');
      setShowNoteDialog(false);
    } catch (error) {
      console.error('Error adding note:', error);
      toast({
        title: 'Error',
        description: 'Failed to add note',
        variant: 'destructive'
      });
    }
  };

  const handleWhatsApp = (lead, templateMessage = null) => {
    const phone = lead.phone?.replace(/[^0-9]/g, '');
    if (!phone) {
      toast({
        title: 'Error',
        description: 'No phone number available',
        variant: 'destructive'
      });
      return;
    }

    const message = templateMessage || `Hi ${lead.name}, how can I help you?`;
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');

    // Update last contacted - optimistic update
    const lastContacted = new Date().toISOString();
    setLeads(leads.map(l => l.id === lead.id ? { ...l, lastContacted } : l));
    
    crmAPI.updateLead(lead.id, { lastContacted }).catch(error => {
      console.error('Error updating last contacted:', error);
    });
  };

  const handleCall = (lead) => {
    if (!lead.phone) {
      toast({
        title: 'Error',
        description: 'No phone number available',
        variant: 'destructive'
      });
      return;
    }
    window.open(`tel:${lead.phone}`, '_self');
    
    // Update last contacted - optimistic update
    const lastContacted = new Date().toISOString();
    setLeads(leads.map(l => l.id === lead.id ? { ...l, lastContacted } : l));
    
    crmAPI.updateLead(lead.id, { lastContacted }).catch(error => {
      console.error('Error updating last contacted:', error);
    });
  };

  const handleAddStage = async () => {
    if (!newStageName.trim()) return;

    // Check permission
    if (!canManagePipelines()) {
      toast({
        title: 'Permission Denied',
        description: 'You do not have permission to manage pipelines',
        variant: 'destructive'
      });
      return;
    }

    try {
      const highestPosition = Math.max(...stages.map(s => s.position), -1);
      const newStage = await crmAPI.createStage({
        name: newStageName,
        color: newStageColor,
        position: highestPosition + 1,
        isDefault: false
      });
      
      // Update local state instead of refetching
      setStages([...stages, newStage]);
      
      toast({
        title: 'Success',
        description: 'Stage added successfully'
      });
      setNewStageName('');
      setNewStageColor('#eab308'); // Reset to yellow color
      setShowAddStageDialog(false);
    } catch (error) {
      console.error('Error adding stage:', error);
      toast({
        title: 'Error',
        description: 'Failed to add stage',
        variant: 'destructive'
      });
    }
  };

  const handleViewContactDetails = async (lead) => {
    if (!lead.contactId) return;

    try {
      let contact = null;
      
      if (lead.contactType === 'physical') {
        const { scannedContactAPI } = await import('../services/api');
        contact = await scannedContactAPI.getById(lead.contactId);
        contact.contactType = 'physical'; // Mark as physical contact
      } else {
        const { contactRelationshipAPI } = await import('../services/api');
        contact = await contactRelationshipAPI.getById(lead.contactId);
        contact.contactType = 'digital'; // Mark as digital contact
      }
      
      setContactDetails(contact);
      setShowContactDialog(true);
    } catch (error) {
      console.error('Error fetching contact details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load contact details',
        variant: 'destructive'
      });
    }
  };

  const handleEditStage = async () => {
    if (!editingStage || !newStageName.trim()) return;

    // Check permission
    if (!canManagePipelines()) {
      toast({
        title: 'Permission Denied',
        description: 'You do not have permission to manage pipelines',
        variant: 'destructive'
      });
      return;
    }

    try {
      await crmAPI.updateStage(editingStage.id, {
        name: newStageName,
        color: newStageColor
      });
      
      // Update local state instead of refetching
      setStages(stages.map(s => 
        s.id === editingStage.id 
          ? { ...s, name: newStageName, color: newStageColor }
          : s
      ));
      
      toast({
        title: 'Success',
        description: 'Stage updated successfully'
      });
      setNewStageName('');
      setNewStageColor('#eab308'); // Reset to yellow color
      setEditingStage(null);
      setShowEditStageDialog(false);
    } catch (error) {
      console.error('Error updating stage:', error);
      toast({
        title: 'Error',
        description: 'Failed to update stage',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteStage = async (stageId) => {
    // Check permission
    if (!canManagePipelines()) {
      toast({
        title: 'Permission Denied',
        description: 'You do not have permission to manage pipelines',
        variant: 'destructive'
      });
      return;
    }

    if (!window.confirm('Are you sure? All leads in this stage will be moved to the first stage.')) return;

    try {
      const result = await crmAPI.deleteStage(stageId);
      
      // Update local state
      setStages(stages.filter(s => s.id !== stageId));
      
      // Update leads that were in this stage if backend returns them
      if (result && result.movedLeads) {
        setLeads(leads.map(l => {
          const movedLead = result.movedLeads.find(ml => ml.id === l.id);
          return movedLead ? { ...l, stageId: movedLead.stageId } : l;
        }));
      } else {
        // Fallback: move to first stage
        const firstStage = stages.find(s => s.id !== stageId);
        if (firstStage) {
          setLeads(leads.map(l => 
            l.stageId === stageId ? { ...l, stageId: firstStage.id } : l
          ));
        }
      }
      
      toast({
        title: 'Success',
        description: 'Stage deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting stage:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete stage',
        variant: 'destructive'
      });
    }
  };

  const handleAddTemplate = async () => {
    if (!newTemplateName.trim() || !newTemplateMessage.trim()) return;

    try {
      const newTemplate = await crmAPI.createTemplate({
        name: newTemplateName,
        message: newTemplateMessage,
        stageId: newTemplateStageId || null
      });
      
      // Update local state instead of refetching
      setTemplates([...templates, newTemplate]);
      
      toast({
        title: 'Success',
        description: 'Template added successfully'
      });
      setNewTemplateName('');
      setNewTemplateMessage('');
      setNewTemplateStageId('');
      setShowAddTemplateDialog(false);
    } catch (error) {
      console.error('Error adding template:', error);
      toast({
        title: 'Error',
        description: 'Failed to add template',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await crmAPI.deleteTemplate(templateId);
      
      // Update local state instead of refetching
      setTemplates(templates.filter(t => t.id !== templateId));
      
      toast({
        title: 'Success',
        description: 'Template deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete template',
        variant: 'destructive'
      });
    }
  };

  const handleSetFollowUp = async () => {
    if (!selectedLead || !followUpDate) return;

    try {
      const updateData = { 
        followUpDate: new Date(followUpDate).toISOString()
      };
      
      // Add follow-up note if provided
      if (followUpNote.trim()) {
        updateData.followUpNote = followUpNote.trim();
      }
      
      await crmAPI.updateLead(selectedLead.id, updateData);
      
      // Update local state instead of refetching
      setLeads(leads.map(l => 
        l.id === selectedLead.id ? { ...l, ...updateData } : l
      ));
      
      // Update selectedLead as well
      setSelectedLead({ ...selectedLead, ...updateData });
      
      toast({
        title: 'Success',
        description: 'Follow-up reminder set'
      });
      setFollowUpDate('');
      setFollowUpNote('');
      setShowFollowUpDialog(false);
    } catch (error) {
      console.error('Error setting follow-up:', error);
      toast({
        title: 'Error',
        description: 'Failed to set follow-up',
        variant: 'destructive'
      });
    }
  };

  // Remove reminder from a deal
  const handleRemoveReminder = async (lead) => {
    if (!lead) return;
    
    try {
      setRemovingReminder(true);
      await crmAPI.removeReminder(lead.id);
      
      // Update local state
      setLeads(leads.map(l => 
        l.id === lead.id ? { ...l, followUpDate: null, followUpNote: null } : l
      ));
      
      // Update selectedLead if it's the same
      if (selectedLead?.id === lead.id) {
        setSelectedLead({ ...selectedLead, followUpDate: null, followUpNote: null });
      }
      
      toast({
        title: 'Success',
        description: 'Reminder removed'
      });
      setShowFollowUpDialog(false);
    } catch (error) {
      console.error('Error removing reminder:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove reminder',
        variant: 'destructive'
      });
    } finally {
      setRemovingReminder(false);
    }
  };

  // Load custom fields for active pipeline
  const loadCustomFields = async () => {
    if (!activePipeline) return;
    try {
      const fields = await crmAPI.getCustomFields(activePipeline.id);
      setCustomFields(fields);
    } catch (error) {
      console.error('Error loading custom fields:', error);
    }
  };

  // Add custom field
  const handleAddCustomField = async () => {
    if (!newFieldName.trim() || !activePipeline) return;
    
    try {
      const fieldData = {
        name: newFieldName.trim(),
        field_type: newFieldType,
        options: newFieldType === 'select' ? newFieldOptions.split(',').map(o => o.trim()).filter(Boolean) : null,
        required: newFieldRequired
      };
      
      await crmAPI.createCustomField(activePipeline.id, fieldData);
      
      // Reload custom fields
      await loadCustomFields();
      
      // Reset form
      setNewFieldName('');
      setNewFieldType('text');
      setNewFieldOptions('');
      setNewFieldRequired(false);
      
      toast({
        title: 'Success',
        description: 'Custom field added'
      });
    } catch (error) {
      console.error('Error adding custom field:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to add custom field',
        variant: 'destructive'
      });
    }
  };

  // Delete custom field
  const handleDeleteCustomField = async (fieldId) => {
    if (!activePipeline) return;
    
    if (!window.confirm('Delete this custom field? This will remove the field from all deals.')) return;
    
    try {
      await crmAPI.deleteCustomField(activePipeline.id, fieldId);
      await loadCustomFields();
      
      toast({
        title: 'Success',
        description: 'Custom field deleted'
      });
    } catch (error) {
      console.error('Error deleting custom field:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete custom field',
        variant: 'destructive'
      });
    }
  };

  const openEditStageDialog = (stage) => {
    setEditingStage(stage);
    setNewStageName(stage.name);
    setNewStageColor(stage.color);
    setShowEditStageDialog(true);
  };

  const handleOpenDetailDialog = (lead) => {
    // Check if user has permission to open this deal
    if (!canOpenDeal(lead)) {
      toast({
        title: "Access Denied",
        description: "You can only view deals assigned to you",
        variant: "destructive"
      });
      return;
    }
    setSelectedLead(lead);
    setShowDetailDialog(true);
    // Load documents for this deal
    loadDealDocuments(lead.id);
  };

  const handleOpenEditDealDialog = (lead) => {
    setSelectedLead(lead);
    setDealName(lead.dealName || '');
    setDealDescription(lead.dealDescription || '');
    setDealAmount(lead.dealAmount || '');
    setDealClosingDate(lead.closingDate ? new Date(lead.closingDate).toISOString().split('T')[0] : '');
    setDealProbability(lead.probability !== null && lead.probability !== undefined ? lead.probability.toString() : '');
    setDealCurrency(lead.currency || '');
    setDealCurrencyRate(lead.currencyRate !== null && lead.currencyRate !== undefined ? lead.currencyRate.toString() : '1');
    setCustomFieldValues(lead.customFields || {});
    setShowEditDealDialog(true);
  };

  const handleSaveDeal = async () => {
    if (!selectedLead) return;

    try {
      const updateData = {
        dealName: dealName || null,
        dealDescription: dealDescription || null,
        dealAmount: dealAmount ? parseFloat(dealAmount) : null,
        closingDate: dealClosingDate ? new Date(dealClosingDate).toISOString() : null,
        probability: dealProbability ? parseFloat(dealProbability) : null,
        currency: dealCurrency || null,
        currencyRate: dealCurrencyRate && dealCurrency ? parseFloat(dealCurrencyRate) : null,
        customFields: Object.keys(customFieldValues).length > 0 ? customFieldValues : null
      };
      
      await crmAPI.updateLead(selectedLead.id, updateData);
      
      // Update local state instead of refetching
      setLeads(leads.map(l => 
        l.id === selectedLead.id ? { ...l, ...updateData } : l
      ));
      
      toast({
        title: 'Success',
        description: 'Deal updated successfully'
      });
      setDealName('');
      setDealDescription('');
      setDealAmount('');
      setDealClosingDate('');
      setDealProbability('');
      setDealCurrency('');
      setDealCurrencyRate('1');
      setCustomFieldValues({});
      setShowEditDealDialog(false);
    } catch (error) {
      console.error('Error updating deal:', error);
      toast({
        title: 'Error',
        description: 'Failed to update deal',
        variant: 'destructive'
      });
    }
  };

  const handleSearchContacts = async (query) => {
    setContactSearchQuery(query);
    
    if (!query.trim()) {
      setSearchedContacts([]);
      return;
    }

    try {
      // Import APIs
      const { contactRelationshipAPI, scannedContactAPI } = await import('../services/api');
      
      const searchLower = query.toLowerCase();
      
      console.log('🔍 Searching contacts with query:', query);
      
      // Search digital contacts - search in name, title, designation, company
      const digitalContacts = await contactRelationshipAPI.getMyContacts();
      console.log('📱 Digital contacts fetched:', digitalContacts.length);
      
      const filteredDigital = digitalContacts
        .filter(c => {
          const name = c.name?.toLowerCase() || '';
          const title = c.title?.toLowerCase() || '';
          const designation = c.designation?.toLowerCase() || '';
          const company = c.company?.toLowerCase() || '';
          
          return name.includes(searchLower) || 
                 title.includes(searchLower) || 
                 designation.includes(searchLower) ||
                 company.includes(searchLower);
        })
        .map(c => ({ ...c, contactType: 'digital', contactId: c.relationship_id }));
      
      console.log('📱 Filtered digital contacts:', filteredDigital.length);
      
      // Search scanned contacts - search in name, title, designation, company
      const scannedResult = await scannedContactAPI.getAll(1, 100, '');
      console.log('📄 Scanned contacts fetched:', scannedResult.data?.length || 0);
      
      const filteredScanned = (scannedResult.data || [])
        .filter(c => {
          const name = c.name?.toLowerCase() || '';
          const title = c.title?.toLowerCase() || '';
          const designation = c.designation?.toLowerCase() || '';
          const company = c.company?.toLowerCase() || '';
          
          return name.includes(searchLower) || 
                 title.includes(searchLower) || 
                 designation.includes(searchLower) ||
                 company.includes(searchLower);
        })
        .map(c => ({ ...c, contactType: 'physical', contactId: c._id }));
      
      console.log('📄 Filtered scanned contacts:', filteredScanned.length);
      console.log('✅ Total contacts found:', filteredDigital.length + filteredScanned.length);
      
      setSearchedContacts([...filteredDigital, ...filteredScanned]);
    } catch (error) {
      console.error('❌ Error searching contacts:', error);
      toast({
        title: 'Error',
        description: 'Failed to search contacts',
        variant: 'destructive'
      });
    }
  };

  const handleAddNewDeal = async () => {
    // Check subscription limit before creating deal
    if (!checkLimit('deals')) {
      window.location.href = '/pricing';
      return;
    }
    
    if (!selectedContact || !dealName.trim() || !selectedStageForNewDeal) {
      toast({
        title: 'Missing Information',
        description: 'Please select a contact, stage, and enter a deal name',
        variant: 'destructive'
      });
      return;
    }

    try {
      const newLead = await crmAPI.createLead({
        name: selectedContact.name,
        phone: selectedContact.phone || null,
        email: selectedContact.email || null,
        stageId: selectedStageForNewDeal,
        contactId: selectedContact.contactId,
        contactType: selectedContact.contactType,
        title: selectedContact.title || selectedContact.designation || null,
        company: selectedContact.company || null,
        isImportant: false,
        dealName: dealName,
        dealDescription: dealDescription || null,
        dealAmount: dealAmount ? parseFloat(dealAmount) : null,
        closingDate: dealClosingDate ? new Date(dealClosingDate).toISOString() : null,
        probability: dealProbability ? parseFloat(dealProbability) : null,
        currency: dealCurrency || null,
        currencyRate: dealCurrencyRate && dealCurrency ? parseFloat(dealCurrencyRate) : null
      });

      // Update local state with functional update for reliability
      setLeads(prevLeads => {
        const updatedLeads = [...prevLeads, newLead];
        console.log('✅ New deal added to state:', {
          newDeal: newLead,
          totalDeals: updatedLeads.length,
          stageId: newLead.stageId,
          pipelineId: newLead.pipelineId,
          activePipelineId: activePipeline?.id
        });
        return updatedLeads;
      });
      
      // Force component re-render to ensure deal appears immediately
      setRenderKey(prev => prev + 1);

      toast({
        title: 'Success',
        description: 'Deal added successfully'
      });

      // Reset form
      setShowAddDealDialog(false);
      setSelectedContact(null);
      setContactSearchQuery('');
      setSearchedContacts([]);
      setSelectedStageForNewDeal('');
      setDealName('');
      setDealDescription('');
      setDealAmount('');
      setDealClosingDate('');
      setDealProbability('');
      setDealCurrency('');
      setDealCurrencyRate('1');
    } catch (error) {
      console.error('Error adding deal:', error);
      toast({
        title: 'Error',
        description: 'Failed to add deal',
        variant: 'destructive'
      });
    }
  };

  const handleMoveStageUp = async (stageIndex) => {
    if (stageIndex === 0) return; // Already at top

    const updatedStages = [...stages];
    // Swap positions
    [updatedStages[stageIndex - 1], updatedStages[stageIndex]] = 
    [updatedStages[stageIndex], updatedStages[stageIndex - 1]];

    // Update positions
    const reorderedStages = updatedStages.map((stage, index) => ({
      id: stage.id,
      position: index
    }));

    // Optimistically update UI
    setStages(updatedStages.map((s, i) => ({ ...s, position: i })));

    try {
      await crmAPI.reorderStages(reorderedStages);
      toast({
        title: 'Success',
        description: 'Stage moved up'
      });
    } catch (error) {
      console.error('Error reordering stages:', error);
      toast({
        title: 'Error',
        description: 'Failed to reorder stages',
        variant: 'destructive'
      });
      fetchData(); // Revert on error
    }
  };

  const handleMoveStageDown = async (stageIndex) => {
    if (stageIndex === stages.length - 1) return; // Already at bottom

    const updatedStages = [...stages];
    // Swap positions
    [updatedStages[stageIndex], updatedStages[stageIndex + 1]] = 
    [updatedStages[stageIndex + 1], updatedStages[stageIndex]];

    // Update positions
    const reorderedStages = updatedStages.map((stage, index) => ({
      id: stage.id,
      position: index
    }));

    // Optimistically update UI
    setStages(updatedStages.map((s, i) => ({ ...s, position: i })));

    try {
      await crmAPI.reorderStages(reorderedStages);
      toast({
        title: 'Success',
        description: 'Stage moved down'
      });
    } catch (error) {
      console.error('Error reordering stages:', error);
      toast({
        title: 'Error',
        description: 'Failed to reorder stages',
        variant: 'destructive'
      });
      fetchData(); // Revert on error
    }
  };

  const handleStageReorder = async (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    if (source.index === destination.index) return;

    // Reorder stages array
    const updatedStages = Array.from(stages);
    const [movedStage] = updatedStages.splice(source.index, 1);
    updatedStages.splice(destination.index, 0, movedStage);

    // Update positions
    const reorderedStages = updatedStages.map((stage, index) => ({
      id: stage.id,
      position: index
    }));

    // Optimistically update UI
    setStages(updatedStages.map((s, i) => ({ ...s, position: i })));

    try {
      await crmAPI.reorderStages(reorderedStages);
      toast({
        title: 'Success',
        description: 'Stages reordered successfully'
      });
    } catch (error) {
      console.error('Error reordering stages:', error);
      toast({
        title: 'Error',
        description: 'Failed to reorder stages',
        variant: 'destructive'
      });
      fetchData(); // Revert on error
    }
  };

  // Import pipeline handlers
  const handleImportClick = () => {
    setShowImportDialog(true);
    setImportFile(null);
    setImportStatus(null);
  };

  const handleImportFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['.csv', '.xlsx', '.xls'];
      const fileExt = '.' + file.name.split('.').pop().toLowerCase();
      if (!validTypes.includes(fileExt)) {
        toast({
          title: "Invalid File Type",
          description: "Please select a CSV or Excel file (.csv, .xlsx, .xls)",
          variant: "destructive"
        });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Maximum file size is 5MB",
          variant: "destructive"
        });
        return;
      }
      setImportFile(file);
    }
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      toast({
        title: "No File Selected",
        description: "Please select a file to import",
        variant: "destructive"
      });
      return;
    }

    setIsImporting(true);
    setImportStatus({ status: 'uploading', message: 'Uploading file...' });

    try {
      const result = await crmAPI.importDeals(importFile);
      
      setImportStatus({
        status: 'processing',
        message: result.message || 'Import started. Deals are being processed...'
      });

      toast({
        title: "Import Started",
        description: `Importing deals to "${result.pipelineName}" pipeline.`
      });

      // Poll for status updates
      let attempts = 0;
      const maxAttempts = 30;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const status = await crmAPI.getImportStatus();
          
          if (status.status === 'completed') {
            clearInterval(pollInterval);
            const stagesMsg = status.stages_created ? ` (${status.stages_created} new stages created)` : '';
            setImportStatus({
              status: 'completed',
              message: `Successfully imported ${status.deals_created || 0} deals!${stagesMsg}`,
              details: status
            });
            setIsImporting(false);
            
            // Refresh data
            fetchData();
            
            toast({
              title: "Import Complete!",
              description: `${status.deals_created || 0} deals imported${stagesMsg}.`
            });
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            setImportStatus({
              status: 'failed',
              message: status.error || 'Import failed. Please try again.',
              details: status
            });
            setIsImporting(false);
            
            toast({
              title: "Import Failed",
              description: status.error || "There was an error importing your deals.",
              variant: "destructive"
            });
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setImportStatus({
              status: 'completed',
              message: 'Import is still processing. Please refresh the page in a moment.'
            });
            setIsImporting(false);
          }
        } catch (error) {
          console.error('Error polling import status:', error);
        }
      }, 1000);

    } catch (error) {
      console.error('Import error:', error);
      setIsImporting(false);
      setImportStatus({
        status: 'failed',
        message: error.response?.data?.detail || 'Failed to upload file. Please try again.'
      });
      
      toast({
        title: "Import Failed",
        description: error.response?.data?.detail || "There was an error uploading your file.",
        variant: "destructive"
      });
    }
  };

  // Deal document handlers
  const loadDealDocuments = async (leadId) => {
    setLoadingDocuments(true);
    try {
      const docs = await crmAPI.getDocuments(leadId);
      setDealDocuments(docs || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      setDealDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedLead) return;

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Maximum file size is 10MB",
        variant: "destructive"
      });
      return;
    }

    setIsUploadingDoc(true);
    try {
      const newDoc = await crmAPI.uploadDocument(selectedLead.id, file);
      setDealDocuments(prev => [newDoc, ...prev]);
      toast({
        title: "Document Uploaded",
        description: `${file.name} uploaded successfully`
      });
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload document",
        variant: "destructive"
      });
    } finally {
      setIsUploadingDoc(false);
      if (docFileInputRef.current) {
        docFileInputRef.current.value = '';
      }
    }
  };

  const handleDocumentDownload = async (doc) => {
    try {
      await crmAPI.downloadDocument(selectedLead.id, doc.id, doc.filename);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download document",
        variant: "destructive"
      });
    }
  };

  const handleDocumentDelete = async (docId) => {
    if (!selectedLead) return;
    
    try {
      await crmAPI.deleteDocument(selectedLead.id, docId);
      setDealDocuments(prev => prev.filter(d => d.id !== docId));
      toast({
        title: "Document Deleted",
        description: "Document removed successfully"
      });
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete document",
        variant: "destructive"
      });
    }
  };

  const getDocumentIcon = (fileType) => {
    if (['.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(fileType)) {
      return <Image className="w-4 h-4 text-green-600" />;
    }
    if (['.xls', '.xlsx', '.csv'].includes(fileType)) {
      return <FileSpreadsheet className="w-4 h-4 text-green-600" />;
    }
    if (['.pdf'].includes(fileType)) {
      return <FileText className="w-4 h-4 text-red-600" />;
    }
    return <File className="w-4 h-4 text-blue-600" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get filtered leads based on assignee filter and member privacy settings
  const getFilteredLeads = () => {
    let filteredLeads = leads;
    const totalLeads = leads.length;
    
    // Filter by current pipeline's stages AND pipelineId
    const currentStageIds = stages.map(stage => stage.id);
    filteredLeads = filteredLeads.filter(lead => {
      // Check if lead belongs to active pipeline
      const belongsToActivePipeline = activePipeline && lead.pipelineId === activePipeline.id;
      const hasValidStage = currentStageIds.includes(lead.stageId);
      return belongsToActivePipeline && hasValidStage;
    });
    console.log(`🔍 Pipeline filter applied: ${filteredLeads.length}/${totalLeads} leads (pipeline: ${activePipeline?.id}, stages: ${currentStageIds.length})`);
    
    // Apply member privacy settings for staff members
    if (team && team.my_role === 'staff' && team.my_settings) {
      if (team.my_settings.deal_visibility === 'assigned_only') {
        // Staff can only see leads assigned to them
        filteredLeads = filteredLeads.filter(lead => lead.assigned_to === userCard?.userId);
        console.log(`🔒 Staff privacy filter applied: ${filteredLeads.length}/${totalLeads} leads visible`);
      }
    }
    
    // Apply user-selected filters
    if (filterAssignees.length > 0) {
      const beforeFilterCount = filteredLeads.length;
      filteredLeads = filteredLeads.filter(lead => {
        // Check each filter option
        for (const filter of filterAssignees) {
          if (filter === 'unassigned' && !lead.assigned_to) return true;
          if (filter === 'me' && lead.assigned_to === userCard?.userId) return true;
          if (lead.assigned_to === filter) return true;
        }
        return false;
      });
      console.log(`🔍 Assignee filter applied:`, {
        filters: filterAssignees,
        before: beforeFilterCount,
        after: filteredLeads.length,
        userCardId: userCard?.userId
      });
    } else {
      console.log(`✨ No assignee filters applied, showing all ${filteredLeads.length} leads`);
    }
    
    // Apply time-based filter (closing date)
    if (timeFilter !== 'all') {
      const beforeTimeFilterCount = filteredLeads.length;
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth(); // 0-indexed (0 = Jan, 11 = Dec)
      
      filteredLeads = filteredLeads.filter(lead => {
        // If no closing date, include in "all" only
        if (!lead.closingDate) return timeFilter === 'all';
        
        const closingDate = new Date(lead.closingDate);
        const closingYear = closingDate.getFullYear();
        const closingMonth = closingDate.getMonth();
        
        if (timeFilter === 'this_month') {
          // Current month and year
          return closingYear === currentYear && closingMonth === currentMonth;
        } else if (timeFilter === 'next_month') {
          // Next month (handle year wrap)
          const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
          const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
          return closingYear === nextYear && closingMonth === nextMonth;
        } else if (timeFilter === 'after_2_months') {
          // 2 months from now and beyond
          const twoMonthsLater = new Date(currentYear, currentMonth + 2, 1);
          return closingDate >= twoMonthsLater;
        }
        
        return true;
      });
      
      console.log(`📅 Time filter applied: ${timeFilter}, ${filteredLeads.length}/${beforeTimeFilterCount} deals`);
    }
    
    // Apply advanced filter (if active)
    if (filteredLeadIds !== null && filteredLeadIds.length >= 0) {
      const beforeAdvancedCount = filteredLeads.length;
      filteredLeads = filteredLeads.filter(lead => filteredLeadIds.includes(lead.id));
      console.log(`🎯 Advanced filter applied: ${filteredLeads.length}/${beforeAdvancedCount} deals`);
    }
    
    return filteredLeads;
  };

  // Toggle assignee filter
  const toggleAssigneeFilter = (value) => {
    setFilterAssignees(prev => {
      const newFilters = prev.includes(value) 
        ? prev.filter(v => v !== value)
        : [...prev, value];
      
      console.log('🔄 Filter toggled:', value);
      console.log('  - Previous filters:', prev);
      console.log('  - New filters:', newFilters);
      
      return newFilters;
    });
  };

  // Clear all filters
  const clearAssigneeFilters = () => {
    console.log('🔄 Clearing all assignee filters');
    setFilterAssignees([]);
  };

  // Apply advanced filter
  const applyAdvancedFilter = async () => {
    if (filterConditions.length === 0) {
      // Clear filter
      setFilteredLeadIds(null);
      setIsFilterActive(false);
      setShowAdvancedFilter(false);
      return;
    }

    try {
      const response = await crmAPI.filterLeads({
        conditions: filterConditions,
        logic: filterLogic,
        pipeline_id: activePipeline?.id
      });

      if (response.data && response.data.leads) {
        const leadIds = response.data.leads.map(l => l.id);
        setFilteredLeadIds(leadIds);
        setIsFilterActive(true);
        setShowAdvancedFilter(false);
        toast({
          title: 'Filter Applied',
          description: `Found ${leadIds.length} matching deals`,
        });
      }
    } catch (error) {
      console.error('Error applying filter:', error);
      toast({
        title: 'Filter Error',
        description: 'Failed to apply filter',
        variant: 'destructive',
      });
    }
  };

  // Clear advanced filter
  const clearAdvancedFilter = () => {
    setFilterConditions([]);
    setFilteredLeadIds(null);
    setIsFilterActive(false);
  };

  // Add a new filter condition
  const addFilterCondition = () => {
    setFilterConditions(prev => [...prev, {
      field: 'name',
      operator: 'contains',
      value: ''
    }]);
  };

  // Update a filter condition
  const updateFilterCondition = (index, updates) => {
    setFilterConditions(prev => prev.map((cond, i) => 
      i === index ? { ...cond, ...updates } : cond
    ));
  };

  // Remove a filter condition
  const removeFilterCondition = (index) => {
    setFilterConditions(prev => prev.filter((_, i) => i !== index));
  };

  // Get available filter fields (standard + custom)
  const getFilterFields = () => {
    const standardFields = [
      { value: 'name', label: 'Deal Name', type: 'text' },
      { value: 'dealAmount', label: 'Deal Amount', type: 'number' },
      { value: 'probability', label: 'Probability (%)', type: 'number' },
      { value: 'closingDate', label: 'Closing Date', type: 'date' },
      { value: 'follow_up', label: 'Follow-up Date', type: 'date' },
      { value: 'assigned_to', label: 'Assigned To', type: 'select' },
      { value: 'source', label: 'Lead Source', type: 'text' },
      { value: 'priority', label: 'Priority', type: 'select' },
    ];

    // Add custom fields from active pipeline
    const customFieldOptions = customFields.map(cf => ({
      value: `customFields.${cf.name}`,
      label: cf.name,
      type: cf.type
    }));

    return [...standardFields, ...customFieldOptions];
  };

  // Get operators based on field type
  const getOperatorsForType = (fieldType) => {
    const textOperators = [
      { value: 'contains', label: 'Contains' },
      { value: 'not_contains', label: 'Does not contain' },
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not equals' },
      { value: 'starts_with', label: 'Starts with' },
      { value: 'ends_with', label: 'Ends with' },
      { value: 'is_empty', label: 'Is empty' },
      { value: 'is_not_empty', label: 'Is not empty' },
    ];

    const numberOperators = [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not equals' },
      { value: 'greater_than', label: 'Greater than' },
      { value: 'less_than', label: 'Less than' },
      { value: 'greater_than_or_equal', label: 'Greater or equal' },
      { value: 'less_than_or_equal', label: 'Less or equal' },
      { value: 'is_empty', label: 'Is empty' },
      { value: 'is_not_empty', label: 'Is not empty' },
    ];

    const dateOperators = [
      { value: 'equals', label: 'Equals' },
      { value: 'greater_than', label: 'After' },
      { value: 'less_than', label: 'Before' },
      { value: 'is_today', label: 'Is today' },
      { value: 'is_this_week', label: 'Is this week' },
      { value: 'is_this_month', label: 'Is this month' },
      { value: 'is_overdue', label: 'Is overdue' },
      { value: 'is_empty', label: 'Is empty' },
      { value: 'is_not_empty', label: 'Is not empty' },
    ];

    const selectOperators = [
      { value: 'equals', label: 'Equals' },
      { value: 'not_equals', label: 'Not equals' },
      { value: 'in', label: 'Is any of' },
      { value: 'not_in', label: 'Is none of' },
      { value: 'is_empty', label: 'Is empty' },
      { value: 'is_not_empty', label: 'Is not empty' },
    ];

    switch (fieldType) {
      case 'number': return numberOperators;
      case 'date': return dateOperators;
      case 'select': return selectOperators;
      default: return textOperators;
    }
  };

  // Check if user can see deal amount based on member settings
  const canSeeDealAmount = (lead) => {
    // Owners can always see amounts
    if (team && team.my_role === 'owner') return true;
    
    // If no team or no settings, show amounts
    if (!team || !team.my_settings) return true;
    
    // Apply amount visibility settings for staff
    if (team.my_role === 'staff' && team.my_settings.amount_visibility === 'assigned_only') {
      return lead.assigned_to === userCard?.userId;
    }
    
    return true;
  };

  // Check if user can open/edit deal based on member settings
  const canOpenDeal = (lead) => {
    // Owners can always open deals
    if (team && team.my_role === 'owner') return true;
    
    // If no team or no settings, allow opening
    if (!team || !team.my_settings) return true;
    
    // Apply card edit permission settings for staff
    if (team.my_role === 'staff' && team.my_settings.card_edit_permission === 'assigned_only') {
      return lead.assigned_to === userCard?.userId;
    }
    
    return true;
  };

  // Check if user can manage pipelines (create/delete)
  const canManagePipelines = () => {
    // Owners can always manage pipelines
    if (team && team.my_role === 'owner') return true;
    
    // If no team, user is solo owner, can manage
    if (!team) return true;
    
    // Staff users check their specific permission
    if (team && team.my_role === 'staff' && team.my_settings) {
      return team.my_settings.can_manage_pipelines === true;
    }
    
    // Default: allow (for solo users)
    return true;
  };

  const getLeadsForStage = (stageId) => {
    const filteredLeads = getFilteredLeads();
    return filteredLeads
      .filter(lead => lead.stageId === stageId)
      .sort((a, b) => {
        // Hot leads (isImportant) always come first
        if (a.isImportant && !b.isImportant) return -1;
        if (!a.isImportant && b.isImportant) return 1;
        // Then sort by position
        return (a.position || 0) - (b.position || 0);
      });
  };

  const isOverdue = (followUpDate) => {
    if (!followUpDate) return false;
    return new Date(followUpDate) < new Date();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now - date;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  if (loading || cardLoading) {
    return (
      <Layout userCard={userCard} loading={cardLoading}>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading CRM...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout userCard={userCard} loading={cardLoading}>
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen py-6">
        <div className="max-w-full mx-auto px-2 sm:px-3 lg:px-4 h-[calc(100vh-8rem)] flex flex-col">
        
        {/* Team Invitation Banner */}
        <TeamInvitationBanner />
        
        {/* Free User Lead Limit Banner */}
        {leadStats && leadStats.max_leads > 0 && (
          <div className={`mb-2 sm:mb-4 px-3 sm:px-4 py-2 sm:py-3 rounded-lg border flex items-center justify-between ${
            leadStats.leads_remaining <= 5 
              ? 'bg-red-50 border-red-200' 
              : leadStats.leads_remaining <= 10 
                ? 'bg-amber-50 border-amber-200' 
                : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-center gap-2">
              <div className={`p-1.5 rounded-full ${
                leadStats.leads_remaining <= 5 
                  ? 'bg-red-100' 
                  : leadStats.leads_remaining <= 10 
                    ? 'bg-amber-100' 
                    : 'bg-blue-100'
              }`}>
                <UserPlus className={`w-4 h-4 ${
                  leadStats.leads_remaining <= 5 
                    ? 'text-red-600' 
                    : leadStats.leads_remaining <= 10 
                      ? 'text-amber-600' 
                      : 'text-blue-600'
                }`} />
              </div>
              <div>
                <p className={`text-sm font-medium ${
                  leadStats.leads_remaining <= 5 
                    ? 'text-red-800' 
                    : leadStats.leads_remaining <= 10 
                      ? 'text-amber-800' 
                      : 'text-blue-800'
                }`}>
                  {leadStats.leads_remaining === 0 
                    ? 'Lead limit reached!' 
                    : `${leadStats.leads_remaining} lead${leadStats.leads_remaining !== 1 ? 's' : ''} remaining`}
                </p>
                <p className="text-xs text-gray-600">
                  Free plan: {leadStats.total_leads_created || leadStats.total_leads}/{leadStats.max_leads} leads used (including deleted)
                </p>
              </div>
            </div>
            <Button
              onClick={() => window.location.href = '/pricing'}
              size="sm"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs px-3"
            >
              <Zap className="w-3 h-3 mr-1" />
              Upgrade
            </Button>
          </div>
        )}
        
        {/* Header - Mobile Optimized */}
        <div className="bg-white shadow-sm border-b flex-shrink-0 z-10 rounded-lg mb-2 sm:mb-4">
          <div className="px-2 sm:px-4 py-2 sm:py-3">
            {/* Mobile: Stack vertically, Desktop: Side by side */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
              {/* Title and Pipeline Selector */}
              <div className="flex items-center justify-between sm:justify-start gap-2 sm:gap-3">
                <div className="flex-1 sm:flex-none">
                  <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">CRM Pipeline</h1>
                  <p className="text-xs text-gray-600 hidden sm:block">Manage your leads</p>
                </div>
                
                {/* Pipeline Selector - Compact on mobile */}
                {activePipeline && (
                  <div className="flex items-center gap-1 sm:gap-2">
                    <button
                      onClick={async () => {
                        setShowPipelineDialog(true);
                        // Lazy load all pipelines when opening dialog
                        await loadPipelines();
                      }}
                      className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-amber-100 to-teal-100 border-2 border-amber-300 rounded-lg hover:border-amber-400 transition-all text-xs sm:text-sm"
                    >
                      <div className="w-1.5 sm:w-2 h-1.5 sm:h-2 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500"></div>
                      <span className="font-semibold text-gray-900 max-w-[80px] sm:max-w-none truncate">{activePipeline.name}</span>
                      <ChevronDown className="w-3 sm:w-4 h-3 sm:h-4 text-gray-600" />
                    </button>
                    {/* Only show create button if user has permission */}
                    {canManagePipelines() && (
                      <button
                        onClick={async () => {
                          // Staff users should check owner's subscription, not see upgrade modal
                          if (team && team.my_role === 'staff') {
                            // For staff, the backend will check owner's subscription
                            // If owner doesn't have subscription, backend will return error
                            setShowCreatePipelineDialog(true);
                            await loadPipelines();
                            return;
                          }
                          
                          // For owners, check if user is subscribed before opening dialog
                          if (!isSubscribed) {
                            window.location.href = '/pricing';
                            return;
                          }
                          setShowCreatePipelineDialog(true);
                          // Lazy load pipeline templates when opening dialog
                          await loadPipelines();
                        }}
                        className="p-1 sm:px-2 sm:py-1.5 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-amber-400 hover:bg-amber-50 transition-all"
                        title="Create new pipeline"
                      >
                        <Plus className="w-3 sm:w-4 h-3 sm:h-4 text-gray-600" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons - Horizontal scroll on mobile */}
              <div className="flex gap-1 sm:gap-2 items-center overflow-x-auto pb-1 sm:pb-0">
                {/* Assignee Filter - Modal Popup with checkboxes */}
                <button
                  onClick={async () => {
                    console.log('👆 Assignee filter button clicked, opening:', !showAssigneeFilter);
                    setShowAssigneeFilter(!showAssigneeFilter);
                    // Lazy load team data when opening filter
                    await loadTeam();
                  }}
                  className={`h-8 px-3 text-xs border rounded-md flex items-center gap-2 min-w-[140px] ${
                    filterAssignees.length === 0 
                      ? 'border-gray-300 bg-white hover:bg-gray-50' 
                      : 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100'
                  }`}
                >
                  <Users className="w-3 h-3" />
                  <span>
                    {filterAssignees.length === 0 
                      ? 'All Users' 
                      : filterAssignees.length === 1 && filterAssignees.includes('me')
                        ? 'Assigned to me'
                        : filterAssignees.length === 1 && filterAssignees.includes('unassigned')
                          ? 'Unassigned'
                          : `${filterAssignees.length} selected`}
                  </span>
                </button>

                {/* Advanced Filter Button */}
                <button
                  onClick={() => setShowAdvancedFilter(true)}
                  className={`relative h-8 px-2 sm:px-3 text-xs border rounded-md flex items-center gap-1 sm:gap-2 min-w-[70px] sm:min-w-[90px] transition-all ${
                    isFilterActive
                      ? 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 ring-2 ring-blue-200'
                      : 'border-gray-300 bg-white hover:bg-gray-50'
                  }`}
                  data-testid="advanced-filter-btn"
                >
                  <div className="relative">
                    <Filter className={`w-3.5 h-3.5 flex-shrink-0 ${isFilterActive ? 'text-blue-600' : ''}`} />
                    {isFilterActive && filterConditions.length > 0 && (
                      <span className="absolute -top-2 -right-2 w-4 h-4 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {filterConditions.length}
                      </span>
                    )}
                  </div>
                  <span className="truncate">{isFilterActive ? 'Filtered' : 'Filter'}</span>
                </button>

                {/* Clear Filter Button - only show when filter is active */}
                {isFilterActive && (
                  <button
                    onClick={clearAdvancedFilter}
                    className="h-8 px-2 text-xs border border-blue-300 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 flex items-center gap-1 transition-all"
                    data-testid="clear-filter-btn"
                    title="Clear filter"
                  >
                    <X className="w-3 h-3" />
                    <span className="hidden sm:inline">Clear</span>
                  </button>
                )}

                {/* Filter Modal Popup */}
                <Dialog open={showAssigneeFilter} onOpenChange={setShowAssigneeFilter}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center justify-between">
                        <span>Filter by Assignee</span>
                        {filterAssignees.length > 0 && (
                          <button
                            onClick={clearAssigneeFilters}
                            className="text-sm text-blue-600 hover:text-blue-700 font-normal"
                          >
                            Clear all
                          </button>
                        )}
                      </DialogTitle>
                      <DialogDescription>
                        Select team members to filter deals
                      </DialogDescription>
                    </DialogHeader>
                    
                    {/* Filter options */}
                    <div className="max-h-96 overflow-y-auto space-y-1 py-4">
                      {/* All Users */}
                      <div 
                        onClick={() => {
                          clearAssigneeFilters();
                          console.log('✨ All Users selected - showing all deals');
                        }}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer rounded-md transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={filterAssignees.length === 0}
                          readOnly
                          className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 pointer-events-none"
                        />
                        <span className="text-base font-semibold text-gray-900">All Users</span>
                      </div>
                      
                      <div className="border-t my-2"></div>
                      
                      {/* Assigned to me */}
                      <label className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer rounded-md transition-colors">
                        <input
                          type="checkbox"
                          checked={filterAssignees.includes('me')}
                          onChange={() => toggleAssigneeFilter('me')}
                          className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-base text-gray-700">Assigned to me</span>
                      </label>
                      
                      {/* Unassigned */}
                      <label className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer rounded-md transition-colors">
                        <input
                          type="checkbox"
                          checked={filterAssignees.includes('unassigned')}
                          onChange={() => toggleAssigneeFilter('unassigned')}
                          className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-base text-gray-700">Unassigned</span>
                      </label>
                      
                      {/* Team members */}
                      {team && team.members && team.members.length > 0 && (
                        <>
                          <div className="border-t my-2"></div>
                          <div className="px-4 py-2">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Team Members</span>
                          </div>
                          {team.members.map(member => (
                            <label 
                              key={member.user_id}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer rounded-md transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={filterAssignees.includes(member.user_id)}
                                onChange={() => toggleAssigneeFilter(member.user_id)}
                                className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                              />
                              <div className="flex items-center gap-3 flex-1">
                                <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-yellow-400 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                                  {member.name ? member.name.charAt(0).toUpperCase() : '?'}
                                </div>
                                <span className="text-base text-gray-700">{member.name || 'Unknown'}</span>
                              </div>
                            </label>
                          ))}
                        </>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                      <Button
                        variant="outline"
                        onClick={() => setShowAssigneeFilter(false)}
                      >
                        Close
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Advanced Filter Dialog */}
                <Dialog open={showAdvancedFilter} onOpenChange={setShowAdvancedFilter}>
                  <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto w-[95vw] sm:w-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Filter className="w-5 h-5 text-blue-600" />
                        Advanced Filter
                      </DialogTitle>
                      <DialogDescription>
                        Create custom filters to find specific deals
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                      {/* Logic selector */}
                      {filterConditions.length > 1 && (
                        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <span className="text-sm text-gray-600">Match</span>
                          <select
                            value={filterLogic}
                            onChange={(e) => setFilterLogic(e.target.value)}
                            className="px-3 py-1.5 border border-blue-200 rounded-md text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="and">All conditions (AND)</option>
                            <option value="or">Any condition (OR)</option>
                          </select>
                        </div>
                      )}
                      
                      {/* Filter conditions */}
                      <div className="space-y-3">
                        {filterConditions.map((condition, index) => {
                          const fieldInfo = getFilterFields().find(f => f.value === condition.field);
                          const fieldType = fieldInfo?.type || 'text';
                          const operators = getOperatorsForType(fieldType);
                          const needsValue = !['is_empty', 'is_not_empty', 'is_today', 'is_this_week', 'is_this_month', 'is_overdue'].includes(condition.operator);
                          
                          return (
                            <div key={index} className="p-3 bg-white border rounded-lg space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-2">
                              {/* Mobile: Stack vertically, Desktop: Row */}
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1">
                                {/* Field selector */}
                                <select
                                  value={condition.field}
                                  onChange={(e) => updateFilterCondition(index, { field: e.target.value, value: '' })}
                                  className="w-full sm:flex-1 px-3 py-2 border rounded-md text-sm bg-white"
                                >
                                  <optgroup label="Standard Fields">
                                    {getFilterFields().filter(f => !f.value.startsWith('customFields.')).map(field => (
                                      <option key={field.value} value={field.value}>{field.label}</option>
                                    ))}
                                  </optgroup>
                                  {customFields.length > 0 && (
                                    <optgroup label="Custom Fields">
                                      {getFilterFields().filter(f => f.value.startsWith('customFields.')).map(field => (
                                        <option key={field.value} value={field.value}>{field.label}</option>
                                      ))}
                                    </optgroup>
                                  )}
                                </select>
                                
                                {/* Operator selector */}
                                <select
                                  value={condition.operator}
                                  onChange={(e) => updateFilterCondition(index, { operator: e.target.value })}
                                  className="w-full sm:w-40 px-3 py-2 border rounded-md text-sm bg-white"
                                >
                                  {operators.map(op => (
                                    <option key={op.value} value={op.value}>{op.label}</option>
                                  ))}
                                </select>
                                
                                {/* Value input - using native input for better mobile support */}
                                {needsValue && (
                                  <input
                                    type={fieldType === 'number' ? 'number' : fieldType === 'date' ? 'date' : 'text'}
                                    value={condition.value || ''}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      updateFilterCondition(index, { value: e.target.value });
                                    }}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="Enter value..."
                                    className="w-full sm:flex-1 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                )}
                              </div>
                              
                              {/* Remove button */}
                              <button
                                onClick={() => removeFilterCondition(index)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors self-end sm:self-auto"
                                title="Remove condition"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Add condition button */}
                      <button
                        onClick={addFilterCondition}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-dashed border-blue-300"
                      >
                        <Plus className="w-4 h-4" />
                        Add Condition
                      </button>
                      
                      {filterConditions.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <Filter className="w-12 h-12 mx-auto mb-3 text-blue-200" />
                          <p>No filter conditions yet</p>
                          <p className="text-sm">Click "Add Condition" to create a filter</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col-reverse sm:flex-row justify-between items-center gap-3 pt-4 border-t">
                      <button
                        onClick={clearAdvancedFilter}
                        className="text-sm text-gray-500 hover:text-gray-700"
                        disabled={filterConditions.length === 0}
                      >
                        Clear All
                      </button>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <Button
                          variant="outline"
                          onClick={() => setShowAdvancedFilter(false)}
                          className="flex-1 sm:flex-none"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={applyAdvancedFilter}
                          disabled={filterConditions.length === 0}
                          className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700"
                        >
                          Apply Filter
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Time Filter Dropdown */}
                <select
                  value={timeFilter}
                  onChange={(e) => setTimeFilter(e.target.value)}
                  className={`h-8 px-3 text-xs border rounded-md flex items-center gap-2 min-w-[140px] ${
                    timeFilter === 'all'
                      ? 'border-gray-300 bg-white hover:bg-gray-50'
                      : 'border-amber-500 bg-amber-50 text-amber-700'
                  }`}
                >
                  <option value="all">All Months</option>
                  <option value="this_month">
                    This Month ({new Date().toLocaleDateString('en-US', { month: 'short' })})
                  </option>
                  <option value="next_month">
                    Next Month ({new Date(new Date().setMonth(new Date().getMonth() + 1)).toLocaleDateString('en-US', { month: 'short' })})
                  </option>
                  <option value="after_2_months">After 2 Months</option>
                </select>

                {/* View Toggle */}
                <div className="flex border border-gray-300 rounded-md overflow-hidden">
                  <button
                    onClick={() => setViewMode('kanban')}
                    className={`px-2 py-1 ${
                      viewMode === 'kanban'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    } transition-colors`}
                    title="Kanban View"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-2 py-1 ${
                      viewMode === 'list'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    } transition-colors`}
                    title="List View"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>

                <Button
                  onClick={() => {
                    setSelectedStageForNewDeal(stages[0]?.id || '');
                    setShowAddDealDialog(true);
                  }}
                  size="sm"
                  className="text-xs h-8 bg-gradient-to-r from-blue-600 to-amber-600 hover:from-blue-700 hover:to-amber-700"
                >
                  <Plus className="w-3 h-3 sm:mr-1" />
                  <span className="hidden sm:inline">Add Deal</span>
                </Button>
                
                {/* Import Pipeline Button */}
                <Button
                  onClick={handleImportClick}
                  variant="outline"
                  size="sm"
                  className="text-xs h-8 border-amber-500 text-amber-700 hover:bg-amber-50"
                >
                  <FileDown className="w-3 h-3 sm:mr-1" />
                  <span className="hidden sm:inline">Import</span>
                </Button>
                
                {canManagePipelines() && (
                  <Button
                    onClick={async () => {
                      setShowManageStagesDialog(true);
                      // Lazy load templates when opening manage dialog
                      await loadTemplates();
                    }}
                    variant="outline"
                    size="sm"
                    className="text-xs h-8"
                  >
                    <Settings className="w-3 h-3 sm:mr-1" />
                    <span className="hidden sm:inline">Manage</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Kanban Board - Fills remaining height */}
        {viewMode === 'kanban' && (
        <div className="flex-1 overflow-hidden p-2 sm:p-3">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="all-stages" direction="horizontal" type="STAGE">
            {(provided, snapshot) => (
              <div 
                {...provided.droppableProps}
                ref={provided.innerRef}
                className={`flex gap-3 h-full pb-4 ${snapshot.isDraggingOver ? 'bg-amber-50/30' : ''}`}
                key={`stages-${leads.length}-${renderKey}`}
                style={{ 
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  minHeight: '100%'
                }}
              >
                {stages.map((stage, stageIndex) => {
                  const stageLeads = getLeadsForStage(stage.id);
                  const isCollapsed = collapsedStages[stage.id];
                  const stageTotal = calculateStageTotal(stage.id);
                  return (
                    <Draggable key={stage.id} draggableId={`stage-${stage.id}`} index={stageIndex}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`flex-shrink-0 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-full relative ${
                            isCollapsed ? 'w-14 cursor-pointer' : ''
                          } ${snapshot.isDragging ? 'shadow-2xl z-[100] ring-2 ring-amber-500 opacity-95' : ''}`}
                          style={{
                            ...provided.draggableProps.style,
                            width: isCollapsed ? '56px' : `${stage.width || 250}px`,
                          }}
                          onClick={isCollapsed ? () => toggleStageCollapse(stage.id) : undefined}
                        >
                          {/* Resize Handle */}
                          {!isCollapsed && (
                            <div
                              className="absolute top-0 right-0 w-1 h-full cursor-col-resize group hover:bg-amber-500 transition-colors z-10"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const startX = e.clientX;
                                const startWidth = stage.width || 250;

                                const handleMouseMove = (moveEvent) => {
                                  const delta = moveEvent.clientX - startX;
                                  const newWidth = Math.max(100, Math.min(300, startWidth + delta));
                                  
                                  // Update locally for immediate feedback
                                  setStages(prev => prev.map(s => 
                                    s.id === stage.id ? { ...s, width: newWidth } : s
                                  ));
                                };

                                const handleMouseUp = async () => {
                                  document.removeEventListener('mousemove', handleMouseMove);
                                  document.removeEventListener('mouseup', handleMouseUp);
                                  
                                  // Save to backend
                                  const finalStage = stages.find(s => s.id === stage.id);
                                  if (finalStage) {
                                    try {
                                      await crmAPI.updateStage(stage.id, { width: finalStage.width });
                                    } catch (error) {
                                      console.error('Error updating stage width:', error);
                                    }
                                  }
                                };

                                document.addEventListener('mousemove', handleMouseMove);
                                document.addEventListener('mouseup', handleMouseUp);
                              }}
                              title="Drag to resize column"
                            >
                              {/* Visual indicator on hover */}
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="w-1 h-8 bg-amber-500 rounded-full"></div>
                              </div>
                            </div>
                          )}
                  {/* Stage Header - Gradient bar at top, white content area */}
                  <div className={`rounded-t-lg ${isCollapsed ? 'h-40' : ''}`}>
                    {/* Gradient color bar at top - uses actual stage color */}
                    <div
                      className="h-2 rounded-t-lg"
                      style={getStageGradientStyle(stage.color)}
                    ></div>
                    
                    {/* Header content with white background */}
                    <div className={`bg-white border-x border-gray-200 ${isCollapsed ? 'h-[calc(100%-8px)]' : 'p-2'}`}>
                    {!isCollapsed ? (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 flex-1 min-w-0">
                            <div 
                              {...provided.dragHandleProps}
                              className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 flex-shrink-0"
                              title="Drag to reorder stages"
                            >
                              <GripVertical className="w-4 h-4" />
                            </div>
                            <h3 className="font-bold text-gray-900 text-sm truncate">{stage.name}</h3>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditStageDialog(stage);
                              }}
                              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-0.5 rounded transition flex-shrink-0"
                              title="Edit stage"
                            >
                              <Edit className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-full text-xs font-semibold flex-shrink-0">
                              {stageLeads.length}
                            </span>
                            <div 
                              onClick={() => toggleStageCollapse(stage.id)}
                              className="cursor-pointer hover:bg-gray-100 p-0.5 rounded transition"
                              title="Collapse stage"
                            >
                              <ChevronLeft className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            </div>
                          </div>
                        </div>
                        {/* Stage Sum/Total Value */}
                        <div className="text-xs text-gray-600 font-medium pl-5">
                          {(() => {
                            const total = stageLeads.reduce((sum, lead) => sum + (lead.dealValue || 0), 0);
                            return total > 0 ? (
                              <span 
                                className="cursor-default"
                                title={`₹${total.toLocaleString('en-IN')}`}
                              >
                                ₹{total.toLocaleString('en-IN')}
                              </span>
                            ) : '';
                          })()}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center w-full h-full p-2">
                        <ChevronRight className="w-4 h-4 text-gray-500 mb-2" />
                        <span 
                          className="text-gray-900 text-sm font-bold mb-2"
                          style={{ 
                            writingMode: 'vertical-rl',
                            textOrientation: 'mixed',
                            transform: 'rotate(180deg)'
                          }}
                        >
                          {stage.name.substring(0, 8)}
                        </span>
                        <span className="bg-gray-100 text-gray-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">
                          {stageLeads.length}
                        </span>
                        {/* Collapsed stage sum */}
                        {(() => {
                          const total = stageLeads.reduce((sum, lead) => sum + (lead.dealValue || 0), 0);
                          return total > 0 ? (
                            <span 
                              className="text-gray-600 text-xs font-medium mt-1 cursor-default"
                              title={`₹${total.toLocaleString('en-IN')}`}
                            >
                              ₹{(total/1000).toFixed(0)}K
                            </span>
                          ) : null;
                        })()}
                      </div>
                    )}
                    </div>
                  </div>

                  {/* Droppable area - always present for both collapsed and expanded states */}
                  <Droppable droppableId={stage.id} type="LEAD">
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`${
                          isCollapsed 
                            ? `h-32 flex items-center justify-center p-2 overflow-hidden ${snapshot.isDraggingOver ? 'bg-amber-50 border-2 border-amber-400 border-dashed' : ''}` 
                            : `flex-1 p-2 space-y-2 ${snapshot.isDraggingOver ? 'bg-amber-50/50' : ''}`
                        }`}
                        style={!isCollapsed ? { overflowY: 'auto', minHeight: 0 } : {}}
                      >
                        {isCollapsed ? (
                          // Collapsed state - show amount and drop indicator
                          <>
                            {stageTotal > 0 && !snapshot.isDraggingOver && (
                              <div className="text-center">
                                <div className="text-green-600 font-bold text-base">
                                  {formatAmount(stageTotal, getCurrencySymbol())}
                                </div>
                              </div>
                            )}
                            {snapshot.isDraggingOver && (
                              <div className="text-center">
                                <div className="text-blue-600 font-semibold text-xs">
                                  Drop here
                                </div>
                              </div>
                            )}
                            {provided.placeholder}
                          </>
                        ) : (
                          // Expanded state - show all leads
                          <>
                            {stageLeads.map((lead, index) => (
                              <Draggable key={lead.id} draggableId={lead.id} index={index}>
                                {(provided, snapshot) => {
                                  const isSwiping = swipeState.isSwiping && swipeState.leadId === lead.id;
                                  const swipeOffset = isSwiping ? swipeState.currentX - swipeState.startX : 0;
                                  
                                  return (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`bg-white border rounded p-2 shadow-sm hover:shadow-md cursor-grab active:cursor-grabbing ${
                                      snapshot.isDragging ? 'shadow-2xl z-[100] ring-2 ring-amber-500 rotate-1 scale-105' : ''
                                    } ${
                                      isOverdue(lead.followUpDate) ? 'border-l-2 border-l-red-500 bg-red-50' : ''
                                    } ${
                                      lead.isImportant ? 'border-l-4 border-l-orange-500 bg-orange-50/30' : ''
                                    }`}
                                    style={provided.draggableProps.style}
                                  >
                                    {/* Minimal Lead Card - Click to see details */}
                                    <div 
                                      className="space-y-1.5"
                                      onClick={(e) => {
                                        // Only open dialog if not dragging
                                        if (!snapshot.isDragging) {
                                          handleOpenDetailDialog(lead);
                                        }
                                      }}
                                    >
                                      {/* Header with Background - Minimal */}
                                      <div className="flex items-center justify-between gap-2 -m-2 p-2 bg-gradient-to-r from-gray-50 to-gray-100 rounded-t">
                                        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                          <div className="flex items-center gap-2">
                                            <h4 className="font-semibold text-gray-900 text-sm truncate" title={lead.name}>{lead.name}</h4>
                                            {lead.dealName && (
                                              <span className="text-xs text-amber-600 truncate font-medium" title={lead.dealName}>💼 {lead.dealName}</span>
                                            )}
                                          </div>
                                          {(lead.title || lead.company) && (
                                            <p className="text-[10px] text-gray-500 truncate">
                                              {lead.title}{lead.title && lead.company && ' • '}{lead.company}
                                            </p>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                          {/* Assigned user avatar */}
                                          {lead.assigned_to && lead.assigned_to_name && (
                                            <div 
                                              className="w-5 h-5 bg-gradient-to-br from-amber-400 to-yellow-400 rounded-full flex items-center justify-center text-white text-[9px] font-semibold cursor-default"
                                              title={`Assigned to: ${lead.assigned_to_name}${lead.assigned_to_designation ? ' (' + lead.assigned_to_designation + ')' : ''}`}
                                            >
                                              {lead.assigned_to_name.charAt(0).toUpperCase()}
                                            </div>
                                          )}
                                          {/* Fire icon */}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleToggleImportant(lead);
                                            }}
                                            className={`transition-all ${
                                              lead.isImportant 
                                                ? 'text-orange-500 hover:text-orange-600' 
                                                : 'text-gray-400 hover:text-orange-500'
                                            }`}
                                            title={lead.isImportant ? 'Remove from hot leads' : 'Mark as hot lead'}
                                          >
                                            <Flame className={`w-4 h-4 ${lead.isImportant ? 'fill-current' : ''}`} />
                                          </button>
                                        </div>
                                      </div>

                                      {/* Divider */}
                                      <div className="border-t border-gray-200 -mx-2"></div>

                                      {/* Minimal Info */}
                                      <div className="px-1 space-y-1">
                                        {/* Deal Amount and Follow-up in same row */}
                                        <div className="flex items-center justify-between gap-2">
                                          {/* Deal Amount - Prominent (with privacy check) */}
                                          {lead.dealAmount && canSeeDealAmount(lead) && (
                                            <p className="text-sm font-bold text-green-600">
                                              {formatDealAmount(lead, true)}
                                            </p>
                                          )}
                                          {lead.dealAmount && !canSeeDealAmount(lead) && (
                                            <p className="text-sm font-bold text-gray-400">
                                              •••
                                            </p>
                                          )}

                                          {/* Follow-up indicator - Compact */}
                                          {lead.followUpDate && (
                                            <div
                                              className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${
                                                isOverdue(lead.followUpDate)
                                                  ? 'bg-red-100 text-red-700'
                                                  : 'bg-blue-100 text-blue-700'
                                              }`}
                                            >
                                              📅 {new Date(lead.followUpDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                            </div>
                                          )}
                                        </div>

                                        {/* Notes indicator */}
                                        {lead.notes && lead.notes.length > 0 && (
                                          <span className="text-[10px] text-gray-500">💭 {lead.notes.length} notes</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  );
                                }}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </>
                        )}
                      </div>
                    )}
                  </Droppable>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        </div>
        )}

        {/* List View - Lead Dashboard Style */}
        {viewMode === 'list' && (
          <div className="flex-1 overflow-auto p-3 sm:p-6 bg-gray-50">
            <div className="max-w-4xl mx-auto">
              {/* Lead Dashboard Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Lead Dashboard</h2>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Live
                </div>
              </div>

              {/* Leads List */}
              <div className="space-y-3">
                {getFilteredLeads().length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                    <p className="text-gray-500">No leads found. Click &quot;Add Deal&quot; to create one.</p>
                  </div>
                ) : (
                  getFilteredLeads().map((lead, index) => {
                    const stage = stages.find(s => s.id === lead.stageId);
                    // Avatar colors cycle through these
                    const avatarColors = [
                      'from-amber-400 to-orange-400',
                      'from-teal-400 to-cyan-500',
                      'from-violet-400 to-purple-500',
                      'from-pink-400 to-rose-500',
                      'from-blue-400 to-indigo-500',
                      'from-emerald-400 to-green-500'
                    ];
                    const avatarColor = avatarColors[index % avatarColors.length];
                    
                    // Determine action icon based on lead info
                    const getActionIcon = () => {
                      if (lead.isImportant) {
                        return { icon: 'bell', color: 'text-amber-500' };
                      } else if (lead.email) {
                        return { icon: 'message', color: 'text-teal-500' };
                      } else if (lead.phone) {
                        return { icon: 'phone', color: 'text-violet-500' };
                      }
                      return { icon: 'eye', color: 'text-gray-400' };
                    };
                    const actionInfo = getActionIcon();
                    
                    // Get a description/source for the lead
                    const getLeadDescription = () => {
                      if (lead.source) return lead.source;
                      if (lead.contactType === 'demo_request') return 'Demo request from bio page';
                      if (lead.dealDescription) return lead.dealDescription.substring(0, 50) + (lead.dealDescription.length > 50 ? '...' : '');
                      if (lead.company) return `From ${lead.company}`;
                      if (lead.notes && lead.notes.length > 0) return lead.notes[0].text?.substring(0, 50);
                      return stage?.name || 'New lead';
                    };
                    
                    return (
                      <div 
                        key={lead.id}
                        onClick={() => handleOpenDetailDialog(lead)}
                        className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-5 cursor-pointer hover:shadow-lg transition-all duration-200 ${
                          lead.isImportant ? 'bg-amber-50/50 border-amber-200' : ''
                        }`}
                        data-testid={`lead-card-${lead.id}`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Avatar with user icon */}
                          <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br ${avatarColor} flex items-center justify-center flex-shrink-0 shadow-md`}>
                            <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          
                          {/* Lead Info */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                              {lead.name || lead.dealName || 'Unknown Lead'}
                            </h3>
                            <p className="text-sm text-gray-500 truncate mt-0.5">
                              {getLeadDescription()}
                            </p>
                          </div>
                          
                          {/* Action Icon */}
                          <div className="flex-shrink-0">
                            {actionInfo.icon === 'bell' && (
                              <svg className={`w-6 h-6 ${actionInfo.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                              </svg>
                            )}
                            {actionInfo.icon === 'message' && (
                              <svg className={`w-6 h-6 ${actionInfo.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                            )}
                            {actionInfo.icon === 'phone' && (
                              <svg className={`w-6 h-6 ${actionInfo.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                            )}
                            {actionInfo.icon === 'eye' && (
                              <Eye className={`w-6 h-6 ${actionInfo.color}`} />
                            )}
                          </div>
                        </div>
                        
                        {/* Additional info row - shown on hover/mobile */}
                        {(lead.dealAmount || lead.phone || lead.followUpDate) && (
                          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                            {lead.dealAmount && canSeeDealAmount(lead) && (
                              <span className="font-semibold text-gray-700">
                                {formatDealAmount(lead, false)}
                              </span>
                            )}
                            {lead.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {lead.phone}
                              </span>
                            )}
                            {lead.followUpDate && (
                              <span className={`flex items-center gap-1 ${isOverdue(lead.followUpDate) ? 'text-red-500' : ''}`}>
                                <Calendar className="w-3 h-3" />
                                {new Date(lead.followUpDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                            <span 
                              className="ml-auto px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ 
                                backgroundColor: `${stage?.color}15`,
                                color: stage?.color || '#6B7280'
                              }}
                            >
                              {stage?.name || 'Unknown'}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

      {/* Add Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>
              Add a note for {selectedLead?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Enter your note here..."
              rows={4}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNoteDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddNote}>Add Note</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send WhatsApp Message</DialogTitle>
            <DialogDescription>
              Choose a template or send a custom message
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {/* Stage-specific templates */}
            {selectedLead && templates
              .filter(t => t.stageId === selectedLead.stageId)
              .map((template) => (
                <button
                  key={template.id}
                  onClick={() => {
                    handleWhatsApp(selectedLead, template.message);
                    setShowTemplateDialog(false);
                  }}
                  className="w-full text-left p-3 border-2 border-blue-200 rounded-lg hover:bg-blue-50 transition"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-sm text-gray-900">{template.name}</p>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Stage Template</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{template.message}</p>
                </button>
              ))}
            
            {/* Global templates */}
            {templates
              .filter(t => !t.stageId)
              .map((template) => (
                <button
                  key={template.id}
                  onClick={() => {
                    handleWhatsApp(selectedLead, template.message);
                    setShowTemplateDialog(false);
                  }}
                  className="w-full text-left p-3 border rounded-lg hover:bg-gray-50 transition"
                >
                  <p className="font-semibold text-sm text-gray-900">{template.name}</p>
                  <p className="text-xs text-gray-600 mt-1">{template.message}</p>
                </button>
              ))}
            
            <Button
              onClick={() => {
                handleWhatsApp(selectedLead);
                setShowTemplateDialog(false);
              }}
              variant="outline"
              className="w-full"
            >
              Send Custom Message
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Stage Dialog */}
      <Dialog open={showAddStageDialog} onOpenChange={setShowAddStageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Stage</DialogTitle>
            <DialogDescription>
              Create a new pipeline stage
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Stage Name</label>
              <Input
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
                placeholder="Enter stage name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Color</label>
              <Input
                type="color"
                value={newStageColor}
                onChange={(e) => setNewStageColor(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAddStageDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddStage}>Add Stage</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Stage Dialog */}
      <Dialog open={showEditStageDialog} onOpenChange={setShowEditStageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Stage</DialogTitle>
            <DialogDescription>
              Update stage details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Stage Name</label>
              <Input
                value={newStageName}
                onChange={(e) => setNewStageName(e.target.value)}
                placeholder="Enter stage name"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Color</label>
              <Input
                type="color"
                value={newStageColor}
                onChange={(e) => setNewStageColor(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEditStageDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleEditStage}>Update Stage</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Stages & Templates Dialog */}
      <Dialog open={showManageStagesDialog} onOpenChange={setShowManageStagesDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Stages & Templates</DialogTitle>
            <DialogDescription>
              Edit or delete stages and WhatsApp templates
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Stages Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Pipeline Stages</h3>
                {canManagePipelines() && (
                  <Button
                    size="sm"
                    onClick={() => {
                      setShowManageStagesDialog(false);
                      setShowAddStageDialog(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Stage
                  </Button>
                )}
              </div>
              <DragDropContext onDragEnd={handleStageReorder}>
                <Droppable droppableId="stages-list">
                  {(provided) => (
                    <div 
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className="space-y-2"
                    >
                      {stages.map((stage, index) => (
                        <Draggable key={stage.id} draggableId={stage.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 ${
                                snapshot.isDragging ? 'shadow-lg bg-white' : ''
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <div 
                                  {...provided.dragHandleProps}
                                  className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                                  title="Drag to reorder"
                                >
                                  <GripVertical className="w-5 h-5" />
                                </div>
                                <div
                                  className="w-4 h-4 rounded"
                                  style={{ backgroundColor: stage.color }}
                                />
                                <span className="font-medium">{stage.name}</span>
                                {stage.isDefault && (
                                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded">
                                    Default
                                  </span>
                                )}
                              </div>
                              {canManagePipelines() && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setShowManageStagesDialog(false);
                                      openEditStageDialog(stage);
                                    }}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 hover:bg-red-50"
                                    onClick={() => handleDeleteStage(stage.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>

            {/* Templates Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">WhatsApp Templates</h3>
                <Button
                  size="sm"
                  onClick={() => {
                    setShowManageStagesDialog(false);
                    setShowAddTemplateDialog(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Template
                </Button>
              </div>
              <div className="space-y-2">
                {templates.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No templates yet</p>
                ) : (
                  templates.map((template) => {
                    const templateStage = template.stageId 
                      ? stages.find(s => s.id === template.stageId)
                      : null;
                    return (
                      <div
                        key={template.id}
                        className="flex items-start justify-between p-3 border rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">{template.name}</p>
                            {templateStage ? (
                              <span 
                                className="text-xs px-2 py-0.5 rounded text-white"
                                style={{ backgroundColor: templateStage.color }}
                              >
                                {templateStage.name}
                              </span>
                            ) : (
                              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                                All Stages
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{template.message}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50 ml-2 flex-shrink-0"
                          onClick={() => {
                            if (window.confirm('Delete this template?')) {
                              handleDeleteTemplate(template.id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Custom Fields Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold">Custom Fields</h3>
                  <p className="text-sm text-gray-500">Add up to 10 custom fields for all deals in this pipeline</p>
                </div>
              </div>
              
              {/* Add new custom field form */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Field Name</label>
                    <Input
                      value={newFieldName}
                      onChange={(e) => setNewFieldName(e.target.value)}
                      placeholder="e.g., Rating, Priority"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Field Type</label>
                    <select
                      value={newFieldType}
                      onChange={(e) => setNewFieldType(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="date">Date</option>
                      <option value="select">Dropdown</option>
                      <option value="rating">Rating (1-5)</option>
                    </select>
                  </div>
                </div>
                
                {newFieldType === 'select' && (
                  <div className="mb-3">
                    <label className="text-sm font-medium text-gray-700">Options (comma separated)</label>
                    <Input
                      value={newFieldOptions}
                      onChange={(e) => setNewFieldOptions(e.target.value)}
                      placeholder="e.g., Hot, Warm, Cold"
                      className="mt-1"
                    />
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newFieldRequired}
                      onChange={(e) => setNewFieldRequired(e.target.checked)}
                      className="w-4 h-4 text-amber-600 rounded"
                    />
                    <span className="text-sm text-gray-700">Required field</span>
                  </label>
                  <Button 
                    onClick={handleAddCustomField}
                    disabled={!newFieldName.trim() || customFields.length >= 10}
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Field
                  </Button>
                </div>
                {customFields.length >= 10 && (
                  <p className="text-xs text-amber-600 mt-2">Maximum 10 custom fields reached</p>
                )}
              </div>

              {/* Existing custom fields list */}
              <div className="space-y-2">
                {customFields.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No custom fields added yet</p>
                ) : (
                  customFields.map((field) => (
                    <div
                      key={field.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                          {field.field_type === 'text' && <span className="text-purple-600 text-sm font-bold">Aa</span>}
                          {field.field_type === 'number' && <span className="text-purple-600 text-sm font-bold">#</span>}
                          {field.field_type === 'date' && <Calendar className="w-4 h-4 text-purple-600" />}
                          {field.field_type === 'select' && <ChevronDown className="w-4 h-4 text-purple-600" />}
                          {field.field_type === 'rating' && <Star className="w-4 h-4 text-purple-600" />}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{field.name}</p>
                          <p className="text-xs text-gray-500">
                            {field.field_type === 'select' && field.options ? `Options: ${field.options.join(', ')}` : field.field_type}
                            {field.required && <span className="text-red-500 ml-1">• Required</span>}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteCustomField(field.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Template Dialog */}
      <Dialog open={showAddTemplateDialog} onOpenChange={setShowAddTemplateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add WhatsApp Template</DialogTitle>
            <DialogDescription>
              Create a new message template for quick sending
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Template Name</label>
              <Input
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="e.g., Follow-Up Message"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Message</label>
              <Textarea
                value={newTemplateMessage}
                onChange={(e) => setNewTemplateMessage(e.target.value)}
                placeholder="Enter your message template..."
                rows={4}
              />
            </div>
            <div>
              <label className="text-sm font-medium">For Stage (Optional)</label>
              <select
                value={newTemplateStageId}
                onChange={(e) => setNewTemplateStageId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">All Stages (Global)</option>
                {stages.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Stage-specific templates appear first when sending messages
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowAddTemplateDialog(false);
                setNewTemplateName('');
                setNewTemplateMessage('');
                setNewTemplateStageId('');
                setShowManageStagesDialog(true);
              }}>
                Cancel
              </Button>
              <Button onClick={handleAddTemplate}>Add Template</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Follow-Up Dialog */}
      <Dialog open={showFollowUpDialog} onOpenChange={(open) => {
        setShowFollowUpDialog(open);
        if (!open) {
          setFollowUpDate('');
          setFollowUpNote('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Follow-Up Reminder</DialogTitle>
            <DialogDescription>
              Choose a date to follow up with {selectedLead?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Show existing reminder if any */}
            {selectedLead?.followUpDate && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-amber-800">Current Reminder</p>
                    <p className="text-sm text-amber-700">
                      {new Date(selectedLead.followUpDate).toLocaleDateString()}
                      {selectedLead.followUpNote && ` - ${selectedLead.followUpNote}`}
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => handleRemoveReminder(selectedLead)}
                    disabled={removingReminder}
                  >
                    {removingReminder ? 'Removing...' : 'Remove'}
                  </Button>
                </div>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium">Follow-Up Date *</label>
              <Input
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Follow-Up Note (Optional)</label>
              <Textarea
                value={followUpNote}
                onChange={(e) => setFollowUpNote(e.target.value)}
                placeholder="Add a note about this follow-up..."
                rows={3}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                This note will help you remember what to discuss during the follow-up
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowFollowUpDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSetFollowUp} disabled={!followUpDate}>
                {selectedLead?.followUpDate ? 'Update Reminder' : 'Set Reminder'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Notes Dialog */}
      <Dialog open={showViewNotesDialog} onOpenChange={setShowViewNotesDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Notes for {selectedLead?.name}</DialogTitle>
            <DialogDescription>
              View all notes and add new ones
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Existing Notes */}
            <div className="space-y-3">
              {selectedLead?.notes && selectedLead.notes.length > 0 ? (
                selectedLead.notes.map((note, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg border">
                    <p className="text-sm text-gray-800">{note.text}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {new Date(note.createdAt).toLocaleString()}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">No notes yet</p>
              )}
            </div>

            {/* Add New Note */}
            <div className="border-t pt-4">
              <label className="text-sm font-medium">Add New Note</label>
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Enter your note here..."
                rows={3}
                className="mt-2"
              />
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" onClick={() => setShowViewNotesDialog(false)}>
                  Close
                </Button>
                <Button onClick={() => {
                  handleAddNote();
                  setShowViewNotesDialog(false);
                }}>
                  Add Note
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog - Professional Tabbed View */}
      <Dialog open={showDetailDialog} onOpenChange={(open) => { setShowDetailDialog(open); if (!open) setDetailTab('details'); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden p-0 flex flex-col">
          {selectedLead && (
            <>
              {/* Header - Clean Professional Design */}
              <div className="bg-gray-900 px-4 py-3 text-white flex items-center justify-between flex-shrink-0">
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-semibold truncate">{selectedLead.name}</h2>
                    {selectedLead.isImportant && (
                      <Flame className="w-4 h-4 fill-amber-500 text-amber-500 flex-shrink-0" />
                    )}
                  </div>
                  {(selectedLead.title || selectedLead.company) && (
                    <p className="text-xs text-gray-400 truncate">
                      {selectedLead.title}{selectedLead.title && selectedLead.company && ' • '}{selectedLead.company}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {selectedLead.contactId && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-300 hover:text-white hover:bg-white/10 h-7 px-2"
                      onClick={() => handleViewContactDetails(selectedLead)}
                      title="View contact"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-gray-300 hover:text-white hover:bg-white/10 h-7 px-2"
                    onClick={() => { setShowDetailDialog(false); handleOpenEditDealDialog(selectedLead); }}
                    title="Edit deal"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <button
                    onClick={() => setShowDetailDialog(false)}
                    className="p-1.5 hover:bg-white/10 rounded transition-colors text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Tabs - Professional 2-color scheme */}
              <div className="flex border-b border-gray-200 bg-white flex-shrink-0">
                <button
                  onClick={() => setDetailTab('details')}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors border-b-2 ${
                    detailTab === 'details' 
                      ? 'border-amber-500 text-amber-700 bg-amber-50/50' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Details
                </button>
                <button
                  onClick={() => setDetailTab('notes')}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors border-b-2 flex items-center justify-center gap-1.5 ${
                    detailTab === 'notes' 
                      ? 'border-amber-500 text-amber-700 bg-amber-50/50' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Notes
                  {selectedLead.notes?.length > 0 && (
                    <span className="bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full text-xs">
                      {selectedLead.notes.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setDetailTab('documents')}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors border-b-2 flex items-center justify-center gap-1.5 ${
                    detailTab === 'documents' 
                      ? 'border-amber-500 text-amber-700 bg-amber-50/50' 
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Documents
                  {dealDocuments.length > 0 && (
                    <span className="bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full text-xs">
                      {dealDocuments.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Tab Content Area - Scrollable */}
              <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
                {/* DETAILS TAB */}
                {detailTab === 'details' && (
                  <div className="space-y-4 h-full">
                    {/* Stage Selector */}
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
                        Deal Stage
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {stages.map((stage) => (
                          <button
                            key={stage.id}
                            onClick={() => handleMoveLead(selectedLead.id, selectedLead.stageId, stage.id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              stage.id === selectedLead.stageId
                                ? 'bg-gray-900 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: stage.color }}
                            ></div>
                            {stage.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  
                    {/* Assignment Info */}
                    {team && (
                      <div className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500 mb-0.5">Assigned To</p>
                            {selectedLead.assigned_to ? (
                              <p className="text-sm font-medium text-gray-900">
                                {selectedLead.assigned_to_name || 'Team Member'}
                              </p>
                            ) : (
                              <p className="text-sm text-gray-400 italic">Not assigned</p>
                            )}
                          </div>
                          {team.my_role === 'owner' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleOpenAssignDialog(selectedLead)}>
                              <UserPlus className="w-3 h-3 mr-1" />
                              {selectedLead.assigned_to ? 'Reassign' : 'Assign'}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Deal Value */}
                    <div className="border border-gray-200 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Deal Value</p>
                      <p className="text-xl font-bold text-gray-900">
                        {selectedLead.dealAmount ? (
                          selectedLead.currency ? (
                            (() => {
                              const currencyInfo = COMMON_CURRENCIES.find(c => c.code === selectedLead.currency);
                              return `${currencyInfo?.symbol || selectedLead.currency}${formatAmount(selectedLead.dealAmount)}`;
                            })()
                          ) : formatCurrency(selectedLead.dealAmount, getCurrencySymbol())
                        ) : `${getCurrencySymbol()}0`}
                      </p>
                      {selectedLead.currency && selectedLead.dealAmount && (
                        <p className="text-sm text-gray-500 mt-0.5">
                          ≈ {formatCurrency(selectedLead.dealAmount * (parseFloat(selectedLead.currencyRate) || 1), getCurrencySymbol())}
                        </p>
                      )}
                      {selectedLead.dealName && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-500 mb-0.5">Deal Name</p>
                          <p className="text-sm font-medium text-gray-900">{selectedLead.dealName}</p>
                        </div>
                      )}
                      {selectedLead.dealDescription && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-500 mb-0.5">Description</p>
                          <p className="text-sm text-gray-700">{selectedLead.dealDescription}</p>
                        </div>
                      )}
                    </div>

                    {/* Additional Details */}
                    {(selectedLead.closingDate || selectedLead.probability !== null) && (
                      <div className="border border-gray-200 rounded-lg p-3">
                        <div className="grid grid-cols-2 gap-3">
                          {selectedLead.closingDate && (
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">Closing Date</p>
                              <p className="text-sm font-medium text-gray-900">
                                {new Date(selectedLead.closingDate).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                          {selectedLead.probability !== null && selectedLead.probability !== undefined && (
                            <div>
                              <p className="text-xs text-gray-500 mb-0.5">Win Probability</p>
                              <p className="text-sm font-medium text-gray-900">{selectedLead.probability}%</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Contact Information */}
                    {(selectedLead.phone || selectedLead.email) && (
                      <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Contact</p>
                        {selectedLead.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900">{selectedLead.phone}</span>
                          </div>
                        )}
                        {selectedLead.email && (
                          <div className="flex items-center gap-2">
                            <MessageCircle className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900">{selectedLead.email}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Follow-up Date */}
                    {selectedLead.followUpDate && (
                      <div className={`border rounded-lg p-3 ${
                        isOverdue(selectedLead.followUpDate) ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'
                      }`}>
                        <div className="flex items-center gap-2">
                          <Calendar className={`w-4 h-4 ${isOverdue(selectedLead.followUpDate) ? 'text-red-600' : 'text-amber-600'}`} />
                          <div>
                            <p className="text-xs text-gray-500">{isOverdue(selectedLead.followUpDate) ? 'Overdue!' : 'Follow-Up'}</p>
                            <p className={`text-sm font-medium ${isOverdue(selectedLead.followUpDate) ? 'text-red-600' : 'text-amber-700'}`}>
                              {new Date(selectedLead.followUpDate).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* NOTES TAB */}
                {detailTab === 'notes' && (
                  <div className="space-y-4 h-full flex flex-col">
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        className="bg-gray-900 hover:bg-gray-800 text-white h-8"
                        onClick={() => { setShowDetailDialog(false); setShowNoteDialog(true); }}
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" />
                        Add Note
                      </Button>
                    </div>
                    
                    {selectedLead.notes && selectedLead.notes.length > 0 ? (
                      <div className="flex-1 space-y-3 overflow-y-auto">
                        {selectedLead.notes.map((note, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-3">
                            <p className="text-sm text-gray-800 leading-relaxed">{note.text}</p>
                            <span className="text-xs text-gray-400 mt-2 block">
                              {new Date(note.createdAt).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center justify-center border border-dashed border-gray-200 rounded-lg">
                        <div className="text-center">
                          <StickyNote className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                          <p className="text-gray-500">No notes yet</p>
                          <p className="text-sm text-gray-400 mt-1">Add a note to track important information</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* DOCUMENTS TAB */}
                {detailTab === 'documents' && (
                  <div className="space-y-4 h-full flex flex-col">
                    <div className="flex justify-end">
                      <input
                        type="file"
                        ref={docFileInputRef}
                        onChange={handleDocumentUpload}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp"
                      />
                      <Button
                        size="sm"
                        className="bg-gray-900 hover:bg-gray-800 text-white h-8"
                        onClick={() => docFileInputRef.current?.click()}
                        disabled={isUploadingDoc}
                      >
                        {isUploadingDoc ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        ) : (
                          <Plus className="w-3.5 h-3.5 mr-1" />
                        )}
                        Upload Document
                      </Button>
                    </div>

                    {loadingDocuments ? (
                      <div className="flex-1 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                      </div>
                    ) : dealDocuments.length > 0 ? (
                      <div className="flex-1 space-y-2 overflow-y-auto">
                        {dealDocuments.map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors group"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {getDocumentIcon(doc.fileType)}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{doc.filename}</p>
                                <p className="text-xs text-gray-400">{formatFileSize(doc.fileSize)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => handleDocumentDownload(doc)}
                                className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDocumentDelete(doc.id)}
                                className="p-1.5 hover:bg-red-50 rounded text-red-600"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center justify-center border border-dashed border-gray-200 rounded-lg">
                        <div className="text-center">
                          <Paperclip className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                          <p className="text-gray-500">No documents yet</p>
                          <p className="text-sm text-gray-400 mt-1">Upload PDFs, images, or spreadsheets</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action Footer - Sticky at Bottom */}
              <div className="border-t border-gray-200 bg-white px-4 py-3 flex-shrink-0">
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => generateAIMessage(selectedLead)}
                    className="flex-1 bg-gray-900 hover:bg-gray-800 text-white h-9 text-sm"
                  >
                    <Zap className="w-4 h-4 mr-1.5" />
                    AI Message
                  </Button>
                  <Button
                    onClick={() => { setShowDetailDialog(false); handleCall(selectedLead); }}
                    variant="outline"
                    className="h-9"
                    size="sm"
                  >
                    <Phone className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={async () => { setShowDetailDialog(false); setShowTemplateDialog(true); await loadTemplates(); }}
                    variant="outline"
                    className="h-9"
                    size="sm"
                  >
                    <MessageCircle className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => {
                      if (selectedLead.followUpDate) {
                        setFollowUpDate(selectedLead.followUpDate.split('T')[0]);
                      } else {
                        setFollowUpDate('');
                      }
                      setFollowUpNote(selectedLead.followUpNote || '');
                      setShowDetailDialog(false);
                      setShowFollowUpDialog(true);
                    }}
                    variant="outline"
                    className="h-9"
                    size="sm"
                  >
                    <Calendar className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => { handleDeleteLead(selectedLead.id); setShowDetailDialog(false); }}
                    variant="outline"
                    className="h-9 text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Deal Dialog */}
      <Dialog open={showEditDealDialog} onOpenChange={setShowEditDealDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Deal Information</DialogTitle>
            <DialogDescription>
              Update deal details for {selectedLead?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Deal Name</label>
              <Input
                value={dealName}
                onChange={(e) => setDealName(e.target.value)}
                placeholder="e.g., Website Development Project"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Deal Amount ({getCurrencySymbol()})</label>
              <Input
                type="number"
                value={dealAmount}
                onChange={(e) => setDealAmount(e.target.value)}
                placeholder="e.g., 50000"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={dealDescription}
                onChange={(e) => setDealDescription(e.target.value)}
                placeholder="Enter deal description..."
                rows={4}
              />
            </div>

            {/* New Fields Row 1: Closing Date and Probability */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Expected Closing Date</label>
                <Input
                  type="date"
                  value={dealClosingDate}
                  onChange={(e) => setDealClosingDate(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Win Probability (%)</label>
                <Input
                  type="number"
                  placeholder="0-100"
                  min="0"
                  max="100"
                  value={dealProbability}
                  onChange={(e) => {
                    const val = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                    setDealProbability(val.toString());
                  }}
                />
              </div>
            </div>

            {/* New Fields Row 2: Currency and Rate */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Currency</label>
                <select
                  value={dealCurrency}
                  onChange={(e) => setDealCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="">Same as team ({getCurrencySymbol()})</option>
                  {COMMON_CURRENCIES.map((curr) => (
                    <option key={curr.code} value={curr.code}>
                      {curr.symbol} {curr.code} - {curr.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">
                  Exchange Rate {dealCurrency && `(${dealCurrency} to ${getCurrencySymbol()})`}
                </label>
                <Input
                  type="number"
                  placeholder="1.0"
                  step="0.01"
                  min="0"
                  value={dealCurrencyRate}
                  onChange={(e) => setDealCurrencyRate(e.target.value)}
                  disabled={!dealCurrency}
                />
                {dealCurrency && (
                  <p className="text-xs text-gray-500 mt-1">
                    1 {dealCurrency} = {dealCurrencyRate || 1} {getCurrencySymbol()}
                  </p>
                )}
              </div>
            </div>

            {/* Custom Fields Section */}
            {customFields.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Custom Fields</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {customFields.map((field) => (
                    <div key={field.id}>
                      <label className="text-sm font-medium">
                        {field.name}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      
                      {field.field_type === 'text' && (
                        <Input
                          value={customFieldValues[field.name] || ''}
                          onChange={(e) => setCustomFieldValues({
                            ...customFieldValues,
                            [field.name]: e.target.value
                          })}
                          placeholder={`Enter ${field.name.toLowerCase()}`}
                          className="mt-1"
                        />
                      )}
                      
                      {field.field_type === 'number' && (
                        <Input
                          type="number"
                          value={customFieldValues[field.name] || ''}
                          onChange={(e) => setCustomFieldValues({
                            ...customFieldValues,
                            [field.name]: e.target.value
                          })}
                          placeholder={`Enter ${field.name.toLowerCase()}`}
                          className="mt-1"
                        />
                      )}
                      
                      {field.field_type === 'date' && (
                        <Input
                          type="date"
                          value={customFieldValues[field.name] || ''}
                          onChange={(e) => setCustomFieldValues({
                            ...customFieldValues,
                            [field.name]: e.target.value
                          })}
                          className="mt-1"
                        />
                      )}
                      
                      {field.field_type === 'select' && (
                        <select
                          value={customFieldValues[field.name] || ''}
                          onChange={(e) => setCustomFieldValues({
                            ...customFieldValues,
                            [field.name]: e.target.value
                          })}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-500"
                        >
                          <option value="">Select {field.name}</option>
                          {(field.options || []).map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      )}
                      
                      {field.field_type === 'rating' && (
                        <div className="flex items-center gap-1 mt-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setCustomFieldValues({
                                ...customFieldValues,
                                [field.name]: star
                              })}
                              className="p-1 hover:scale-110 transition-transform"
                            >
                              <Star 
                                className={`w-6 h-6 ${
                                  (customFieldValues[field.name] || 0) >= star 
                                    ? 'text-yellow-400 fill-yellow-400' 
                                    : 'text-gray-300'
                                }`}
                              />
                            </button>
                          ))}
                          {customFieldValues[field.name] && (
                            <button
                              type="button"
                              onClick={() => setCustomFieldValues({
                                ...customFieldValues,
                                [field.name]: null
                              })}
                              className="ml-2 text-xs text-gray-500 hover:text-red-500"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowEditDealDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveDeal}>Save Deal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Deal Dialog */}
      <Dialog open={showAddDealDialog} onOpenChange={setShowAddDealDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Deal</DialogTitle>
            <DialogDescription>
              Search for a contact and create a deal
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Limit Warning Banner */}
            {!limitsLoading && !checkLimit('deals') && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-bold text-gray-900 mb-1">
                      Deal Limit Reached
                    </h4>
                    <p className="text-xs text-gray-700 mb-2">
                      You&apos;ve reached the maximum of {limits.deals.max} deals allowed on the free plan. Upgrade to create unlimited deals.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        setShowAddDealDialog(false);
                        window.location.href = '/pricing';
                      }}
                      className="bg-gradient-to-r from-amber-600 to-pink-600 hover:from-amber-700 hover:to-pink-700 text-white text-xs"
                    >
                      <Zap className="w-3 h-3 mr-1" />
                      Upgrade Now
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Contact Search */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Search Contact
              </label>
              <Input
                type="text"
                placeholder="Search by name..."
                value={contactSearchQuery}
                onChange={(e) => handleSearchContacts(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Search Results */}
            {searchedContacts.length > 0 && !selectedContact && (
              <div className="border rounded-lg max-h-60 overflow-y-auto">
                <div className="divide-y">
                  {searchedContacts.map((contact) => (
                    <div
                      key={contact.contactId}
                      onClick={() => {
                        setSelectedContact(contact);
                        setSearchedContacts([]);
                        setContactSearchQuery(contact.name);
                      }}
                      className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{contact.name}</p>
                          {(contact.title || contact.designation || contact.company) && (
                            <p className="text-sm text-gray-600 mt-0.5 truncate">
                              {contact.title || contact.designation}
                              {(contact.title || contact.designation) && contact.company && ' • '}
                              {contact.company}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {contact.email || contact.phone || 'No contact info'}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded flex-shrink-0 ${
                          contact.contactType === 'digital' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {contact.contactType === 'digital' ? 'Digital' : 'Scanned'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Contact */}
            {selectedContact && (
              <div className="border rounded-lg p-3 bg-blue-50">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900">{selectedContact.name}</p>
                    {(selectedContact.title || selectedContact.designation || selectedContact.company) && (
                      <p className="text-sm text-gray-700 mt-0.5">
                        {selectedContact.title || selectedContact.designation}
                        {(selectedContact.title || selectedContact.designation) && selectedContact.company && ' • '}
                        {selectedContact.company}
                      </p>
                    )}
                    <p className="text-xs text-gray-600 mt-1">
                      {selectedContact.email || selectedContact.phone}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedContact(null);
                      setContactSearchQuery('');
                    }}
                    className="text-red-600 hover:text-red-800 flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Deal Details */}
            {selectedContact && (
              <>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Deal Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter deal name"
                    value={dealName}
                    onChange={(e) => setDealName(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Stage <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedStageForNewDeal}
                    onChange={(e) => setSelectedStageForNewDeal(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a stage</option>
                    {stages.map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Deal Amount ({getCurrencySymbol()})
                  </label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={dealAmount}
                    onChange={(e) => setDealAmount(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    Description
                  </label>
                  <Textarea
                    placeholder="Enter deal description..."
                    value={dealDescription}
                    onChange={(e) => setDealDescription(e.target.value)}
                    className="w-full"
                    rows={3}
                  />
                </div>

                {/* New Fields Row 1: Closing Date and Probability */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Expected Closing Date
                    </label>
                    <Input
                      type="date"
                      value={dealClosingDate}
                      onChange={(e) => setDealClosingDate(e.target.value)}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Win Probability (%)
                    </label>
                    <Input
                      type="number"
                      placeholder="0-100"
                      min="0"
                      max="100"
                      value={dealProbability}
                      onChange={(e) => {
                        const val = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                        setDealProbability(val.toString());
                      }}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* New Fields Row 2: Currency and Rate */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Currency
                    </label>
                    <select
                      value={dealCurrency}
                      onChange={(e) => setDealCurrency(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="">Same as team ({getCurrencySymbol()})</option>
                      {COMMON_CURRENCIES.map((curr) => (
                        <option key={curr.code} value={curr.code}>
                          {curr.symbol} {curr.code} - {curr.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-2 block">
                      Exchange Rate {dealCurrency && `(${dealCurrency} to ${getCurrencySymbol()})`}
                    </label>
                    <Input
                      type="number"
                      placeholder="1.0"
                      step="0.01"
                      min="0"
                      value={dealCurrencyRate}
                      onChange={(e) => setDealCurrencyRate(e.target.value)}
                      className="w-full"
                      disabled={!dealCurrency}
                    />
                    {dealCurrency && (
                      <p className="text-xs text-gray-500 mt-1">
                        1 {dealCurrency} = {dealCurrencyRate || 1} {getCurrencySymbol()}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAddDealDialog(false);
                      setSelectedContact(null);
                      setContactSearchQuery('');
                      setSearchedContacts([]);
                      setSelectedStageForNewDeal('');
                      setDealName('');
                      setDealDescription('');
                      setDealAmount('');
                      setDealClosingDate('');
                      setDealProbability('');
                      setDealCurrency('');
                      setDealCurrencyRate('1');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddNewDeal}
                    className="bg-gradient-to-r from-blue-600 to-amber-600 hover:from-blue-700 hover:to-amber-700"
                  >
                    Add Deal
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Details Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contact Details</DialogTitle>
            <DialogDescription>
              {contactDetails?.contactType === 'physical' ? 'Scanned Business Card' : 'Digital Contact'}
            </DialogDescription>
          </DialogHeader>
          {contactDetails && (
            <div className="space-y-4">
              {/* Image for Scanned Contacts */}
              {contactDetails.contactType === 'physical' && contactDetails.imagePath && (
                <div className="flex justify-center">
                  <img 
                    src={contactDetails.imagePath} 
                    alt={contactDetails.name}
                    className="w-full max-w-md rounded-lg shadow-md object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Name */}
              <div className="border-b pb-3">
                <h3 className="text-xl font-bold text-gray-900">{contactDetails.name}</h3>
                {(contactDetails.title || contactDetails.company || contactDetails.designation) && (
                  <p className="text-sm text-gray-600 mt-1">
                    {contactDetails.title || contactDetails.designation}
                    {(contactDetails.title || contactDetails.designation) && contactDetails.company && ' • '}
                    {contactDetails.company}
                  </p>
                )}
              </div>

              {/* Bio for Digital Contacts (prominent display) */}
              {contactDetails.contactType === 'digital' && contactDetails.bio && (
                <div className="bg-gradient-to-r from-blue-50 to-amber-50 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <StickyNote className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-700 font-semibold mb-1">About</p>
                      <p className="text-sm text-gray-900 leading-relaxed">{contactDetails.bio}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div className="space-y-3">
                {contactDetails.phone && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Phone</p>
                      <p className="text-sm text-gray-900">{contactDetails.phone}</p>
                    </div>
                  </div>
                )}

                {contactDetails.email && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Email</p>
                      <p className="text-sm text-gray-900">{contactDetails.email}</p>
                    </div>
                  </div>
                )}

                {contactDetails.website && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ExternalLink className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Website</p>
                      <a 
                        href={contactDetails.website.startsWith('http') ? contactDetails.website : `https://${contactDetails.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {contactDetails.website}
                      </a>
                    </div>
                  </div>
                )}

                {contactDetails.address && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">📍</span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Address</p>
                      <p className="text-sm text-gray-900">{contactDetails.address}</p>
                    </div>
                  </div>
                )}

                {/* Bio for Physical Contacts (less prominent) */}
                {contactDetails.contactType === 'physical' && contactDetails.bio && (
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <StickyNote className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Notes</p>
                      <p className="text-sm text-gray-900">{contactDetails.bio}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Social Links */}
              {(contactDetails.linkedin || contactDetails.twitter || contactDetails.instagram) && (
                <div className="border-t pt-3">
                  <p className="text-xs text-gray-500 font-medium mb-2">Social Links</p>
                  <div className="flex gap-2 flex-wrap">
                    {contactDetails.linkedin && (
                      <a
                        href={contactDetails.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                      >
                        LinkedIn
                      </a>
                    )}
                    {contactDetails.twitter && (
                      <a
                        href={contactDetails.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-sky-500 text-white text-xs rounded hover:bg-sky-600"
                      >
                        Twitter
                      </a>
                    )}
                    {contactDetails.instagram && (
                      <a
                        href={contactDetails.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-pink-600 text-white text-xs rounded hover:bg-pink-700"
                      >
                        Instagram
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Lead</DialogTitle>
            <DialogDescription>
              Assign this lead to a team member or yourself
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {leadToAssign && (
              <div className="bg-gray-50 border rounded-lg p-3">
                <p className="text-sm font-semibold text-gray-900">{leadToAssign.name}</p>
                <p className="text-xs text-gray-600">{leadToAssign.title || leadToAssign.designation}</p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium">Assign To</label>
              <select
                value={selectedAssignee}
                onChange={(e) => setSelectedAssignee(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="">-- Unassigned --</option>
                {team?.members?.map((member) => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.name} ({member.designation})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium">Assignment Note (Optional)</label>
              <Textarea
                value={assignmentNote}
                onChange={(e) => setAssignmentNote(e.target.value)}
                placeholder="Add a note about this assignment..."
                className="mt-1"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAssignLead}>
                {selectedAssignee ? 'Assign Lead' : 'Remove Assignment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI WhatsApp Message Dialog */}
      <Dialog open={showAIMessageDialog} onOpenChange={setShowAIMessageDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <span className="text-2xl">✨</span>
              AI-Generated WhatsApp Message
              <MessageCircle className="w-5 h-5 text-green-600" />
            </DialogTitle>
            <DialogDescription>
              Personalized message based on deal stage and information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {generatingMessage ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-gray-600 font-medium">Generating your personalized message...</p>
                <p className="text-sm text-gray-500 mt-2">Analyzing deal information and stage</p>
              </div>
            ) : (
              <>
                {/* Deal Info Summary */}
                {selectedLead && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">
                          {selectedLead.name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{selectedLead.name}</h4>
                        {selectedLead.company && (
                          <p className="text-sm text-gray-600">{selectedLead.company}</p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedLead.dealName && (
                            <span className="text-xs bg-white border border-green-300 text-green-700 px-2 py-1 rounded-full">
                              {selectedLead.dealName}
                            </span>
                          )}
                          {selectedLead.dealAmount && (
                            <span className="text-xs bg-white border border-green-300 text-green-700 px-2 py-1 rounded-full font-semibold">
                              {formatCurrency(selectedLead.dealAmount, getCurrencySymbol())}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Generated Message */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-green-600" />
                    Your Message (Editable)
                  </label>
                  <Textarea
                    value={aiGeneratedMessage}
                    onChange={(e) => setAiGeneratedMessage(e.target.value)}
                    className="min-h-[300px] font-sans text-sm leading-relaxed"
                    placeholder="Generated message will appear here..."
                  />
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      ✅ Professional tone
                    </span>
                    <span className="inline-flex items-center gap-1">
                      ✅ Stage-specific context
                    </span>
                    <span className="inline-flex items-center gap-1">
                      ✅ Deal information included
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowAIMessageDialog(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (selectedLead?.phone) {
                        sendWhatsAppMessage(selectedLead.phone, aiGeneratedMessage);
                        setShowAIMessageDialog(false);
                        toast({
                          title: "Opening WhatsApp",
                          description: "Message ready to send!"
                        });
                      } else {
                        toast({
                          title: "No Phone Number",
                          description: "This lead doesn't have a phone number.",
                          variant: "destructive"
                        });
                      }
                    }}
                    disabled={!aiGeneratedMessage || !selectedLead?.phone}
                    className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Send via WhatsApp
                  </Button>
                </div>

                {!selectedLead?.phone && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-sm text-orange-800 flex items-center gap-2">
                      <span className="text-base">⚠️</span>
                      <span>
                        <strong>No phone number available.</strong> Add a phone number to this lead to send WhatsApp messages.
                      </span>
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pipeline Switcher Dialog */}
      <Dialog open={showPipelineDialog} onOpenChange={setShowPipelineDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-600" />
              Switch Pipeline
            </DialogTitle>
            <DialogDescription>
              Select which pipeline you want to work with
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 py-4">
            {pipelines.map((pipeline) => (
              <button
                key={pipeline.id}
                onClick={() => handleSwitchPipeline(pipeline.id)}
                disabled={pipeline.is_active}
                className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                  pipeline.is_active
                    ? 'border-amber-500 bg-amber-50 cursor-default'
                    : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className={`w-3 h-3 rounded-full ${
                      pipeline.is_active 
                        ? 'bg-gradient-to-r from-amber-500 to-yellow-500 ring-2 ring-amber-300 ring-offset-2' 
                        : 'bg-gray-300'
                    }`}
                  ></div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">{pipeline.name}</p>
                    <p className="text-xs text-gray-500">{pipeline.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {pipeline.is_active && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                      Active
                    </span>
                  )}
                  {!pipeline.is_active && pipelines.length > 1 && canManagePipelines() && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeletePipeline(pipeline.id);
                      }}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title="Delete pipeline"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </button>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowPipelineDialog(false)}>
              Close
            </Button>
            {canManagePipelines() && (
              <Button 
                onClick={() => {
                  // Check if user is subscribed before opening dialog
                  if (!isSubscribed) {
                    setShowPipelineDialog(false);
                    window.location.href = '/pricing';
                    return;
                  }
                  setShowPipelineDialog(false);
                  setShowCreatePipelineDialog(true);
                }}
                className="bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create New Pipeline
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Pipeline Dialog */}
      <Dialog open={showCreatePipelineDialog} onOpenChange={setShowCreatePipelineDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Zap className="w-6 h-6 text-amber-600" />
              Create New Pipeline
            </DialogTitle>
            <DialogDescription>
              Choose a template to get started quickly with predefined stages
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Template Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pipelineTemplates.map((template) => (
                <button
                  key={template.key}
                  onClick={() => setSelectedTemplate(template.key)}
                  className={`text-left p-5 rounded-xl border-2 transition-all ${
                    selectedTemplate === template.key
                      ? 'border-amber-500 bg-amber-50 shadow-md'
                      : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-lg text-gray-900 mb-1">{template.name}</h3>
                      <p className="text-sm text-gray-600">{template.description}</p>
                    </div>
                    {selectedTemplate === template.key && (
                      <div className="w-6 h-6 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="px-2 py-1 bg-gray-100 rounded-full font-medium">
                      {template.stage_count} stages
                    </span>
                    <span>•</span>
                    <span className="capitalize">{template.key.replace('_', ' ')}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Selected Template Preview */}
            {selectedTemplate && (
              <div className="bg-gradient-to-r from-amber-50 to-teal-50 border-2 border-amber-200 rounded-xl p-5">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4 text-amber-600" />
                  Template Preview
                </h4>
                <div className="flex flex-wrap gap-2">
                  {(() => {
                    const template = pipelineTemplates.find(t => t.key === selectedTemplate);
                    // Show stage preview (we'll need to fetch or show basic info)
                    const stageNames = {
                      'default': ['New Lead', 'Contacted', 'Qualified', 'Proposal', 'Closed Won', 'Closed Lost'],
                      'sales': ['Qualification', 'Need Analysis', 'Proposal/Price Quote', 'Negotiation/Review', 'Closed Won', 'Closed Lost'],
                      'support': ['New Ticket', 'In Progress', 'On Hold', 'Closed', 'Deferred', 'Not an Issue'],
                      'recruitment': ['Shortlisted', 'Screening', 'Interview Process', 'Offered', 'Hired', 'Rejected'],
                      'it_requests': ['New Request', 'Assigned to Technician', 'Awaiting Vendor', 'On Hold', 'Closed', 'Invalid Request'],
                      'project': ['New Task', 'Working', 'On Hold', 'Completed', 'Cancelled']
                    };
                    return stageNames[selectedTemplate]?.map((stageName, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-white border border-amber-200 text-sm text-gray-700 rounded-full"
                      >
                        {stageName}
                      </span>
                    ));
                  })()}
                </div>
              </div>
            )}

            {/* Custom Name Input */}
            <div>
              <label className="text-sm font-semibold text-gray-900 mb-2 block">
                Custom Pipeline Name (Optional)
              </label>
              <Input
                value={customPipelineName}
                onChange={(e) => setCustomPipelineName(e.target.value)}
                placeholder="Leave empty to use template name"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">
                If not specified, the template name will be used
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCreatePipelineDialog(false);
                setSelectedTemplate(null);
                setCustomPipelineName('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreatePipeline}
              disabled={!selectedTemplate}
              className="bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white"
            >
              <Zap className="w-4 h-4 mr-2" />
              Create Pipeline
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Pipeline Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="w-5 h-5 text-amber-600" />
              Import Pipeline Deals
            </DialogTitle>
            <DialogDescription>
              Import deals from a CSV or Excel file into &quot;{activePipeline?.name || 'Active Pipeline'}&quot;
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Instructions */}
            <div className="text-sm text-gray-600 space-y-2">
              <div className="bg-gray-50 rounded-lg p-3 text-xs">
                <p className="font-medium text-gray-700 mb-1">Supported columns:</p>
                <p className="text-gray-500">
                  Deal Name, Company Name, Contact Name, Stage, Amount, Closing Date, Description
                </p>
                <p className="text-gray-400 mt-2">
                  • New stages will be automatically created if not found<br/>
                  • All deals will be assigned to you
                </p>
              </div>
            </div>

            {/* File Input */}
            <div className="space-y-2">
              <input
                type="file"
                ref={importFileInputRef}
                onChange={handleImportFileSelect}
                accept=".csv,.xlsx,.xls"
                className="hidden"
                disabled={isImporting}
              />
              
              <div 
                onClick={() => !isImporting && importFileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                  ${importFile ? 'border-amber-500 bg-amber-50' : 'border-gray-300 hover:border-amber-400 hover:bg-gray-50'}
                  ${isImporting ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                {importFile ? (
                  <div className="space-y-1">
                    <FileText className="w-8 h-8 mx-auto text-amber-600" />
                    <p className="font-medium text-gray-900">{importFile.name}</p>
                    <p className="text-sm text-gray-500">
                      {(importFile.size / 1024).toFixed(1)} KB
                    </p>
                    {!isImporting && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); setImportFile(null); }}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <FileDown className="w-8 h-8 mx-auto text-gray-400" />
                    <p className="text-gray-600">Click to select a file</p>
                    <p className="text-xs text-gray-400">CSV or Excel (max 5MB)</p>
                  </div>
                )}
              </div>
            </div>

            {/* Import Status */}
            {importStatus && (
              <div className={`rounded-lg p-3 text-sm ${
                importStatus.status === 'completed' ? 'bg-green-50 text-green-700' :
                importStatus.status === 'failed' ? 'bg-red-50 text-red-700' :
                'bg-amber-50 text-amber-700'
              }`}>
                <div className="flex items-center gap-2">
                  {importStatus.status === 'completed' ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : importStatus.status === 'failed' ? (
                    <X className="w-4 h-4" />
                  ) : (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  <span>{importStatus.message}</span>
                </div>
                {importStatus.details?.errors?.length > 0 && (
                  <div className="mt-2 text-xs">
                    <p className="font-medium">Errors ({importStatus.details.errors.length}):</p>
                    <ul className="list-disc ml-4 mt-1 max-h-20 overflow-y-auto">
                      {importStatus.details.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {importStatus.details.errors.length > 5 && (
                        <li>...and {importStatus.details.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowImportDialog(false)}
                disabled={isImporting}
              >
                {importStatus?.status === 'completed' ? 'Close' : 'Cancel'}
              </Button>
              {importStatus?.status !== 'completed' && (
                <Button
                  onClick={handleImportSubmit}
                  disabled={!importFile || isImporting}
                  className="bg-gradient-to-r from-amber-600 to-yellow-500 text-white hover:from-amber-700 hover:to-yellow-600"
                >
                  {isImporting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <FileDown className="w-4 h-4 mr-2" />
                      Import Deals
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      </div>
      </div>
    </Layout>
  );
};

export default CRM;
